import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import noTemplateReasons from '../lib/noTemplateReasons.json'
import { useToast } from '../ui/ToastProvider'
import { previewFontFamilyForMappingKey } from '../lib/pdfMappingFontOptions'
import { formatResidentDisplay } from '../lib/claimContactFormat'
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist'
import { formatDate } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'

// pdf.js worker 설정
// - CRA/Webpack 5에서 require(worker)가 문자열 URL이 아닐 수 있음 → public/pdf.worker.min.js(동일 출처) 사용.
// - 외부 CDN은 CSP(script-src)에 없으면 차단되므로 쓰지 않는다. (postinstall/prestart에서 public으로 복사)
function resolvePdfjsWorkerSrc() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  const fromPublic = `${base}/pdf.worker.min.js`
  try {
    // eslint-disable-next-line global-require
    const workerModule = require('pdfjs-dist/build/pdf.worker.min.js')
    const u = workerModule?.default ?? workerModule
    if (typeof u === 'string' && u.trim().length > 0) return u.trim()
  } catch {
    /* ignore */
  }
  return fromPublic
}
GlobalWorkerOptions.workerSrc = resolvePdfjsWorkerSrc()

function isPdfJsWorkerLoadError(e) {
  const msg = String(e?.message || e || '')
  return (
    msg.includes('Setting up fake worker failed') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Failed to load module script') ||
    msg.includes('Strict MIME type checking') ||
    msg.includes('Unexpected token') ||
    msg.includes('Content Security Policy') ||
    msg.includes('CSP')
  )
}

async function getDocumentWithWorkerFallback(opts) {
  // 한화생명 등 일부 PDF에서 텍스트가 보이지 않는 문제 해결을 위한 옵션 추가
  // - useSystemFonts: true로 설정하여 시스템 폰트를 사용 (폰트 임베딩 문제 해결)
  // - disableFontFace: false로 설정하여 폰트 로딩 활성화
  const enhancedOpts = {
    ...opts,
    useSystemFonts: true, // 시스템 폰트 사용으로 폰트 임베딩 문제 해결
    disableFontFace: false, // 폰트 로딩 활성화
    verbosity: 0, // 로그 레벨 (0=오류만, 1=경고, 2=정보, 3=디버그)
  }
  
  let loadingTask = null
  try {
    loadingTask = getDocument(enhancedOpts)
  } catch (e) {
    // getDocument가 동기 throw하는 환경(워커 생성 즉시 실패 등)도 폴백 대상
    if (isPdfJsWorkerLoadError(e)) {
      const loadingTask2 = getDocument({ ...enhancedOpts, disableWorker: true })
      const pdf2 = await loadingTask2.promise
      return { loadingTask: loadingTask2, pdf: pdf2, usedFallback: true }
    }
    throw e
  }
  try {
    const pdf = await loadingTask.promise
    return { loadingTask, pdf, usedFallback: false }
  } catch (e) {
    try { loadingTask.destroy?.() } catch { }
    if (!isPdfJsWorkerLoadError(e)) throw e
    // 워커가 어떤 이유로든(서버 MIME/CORS/정책 등) 로드 실패하면, disableWorker로 재시도(느리지만 안정적)
    const loadingTask2 = getDocument({ ...enhancedOpts, disableWorker: true })
    const pdf2 = await loadingTask2.promise
    return { loadingTask: loadingTask2, pdf: pdf2, usedFallback: true }
  }
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function ymdCompact(date) {
  const d = (date instanceof Date) ? date : new Date(date)
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`
}

function todayIso() {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function todayParts() {
  const d = new Date()
  return {
    yyyy: String(d.getFullYear()),
    mm: pad2(d.getMonth() + 1),
    dd: pad2(d.getDate()),
  }
}

function formatKst(iso) {
  return formatDate(iso)
}

function defaultConsentTemplateFields() {
  // 기본 필드: 고객명 + 체크박스 5개 (좌표는 임시값, fontSize 기본 16)
  return [
    { key: 'customerName', bindKey: 'customerName', pageIndex: 0, x: 0.1, y: 0.1, w: 0.3, h: 0.03, fontSize: 16, align: 'left', valueType: '', value: '' },
    { key: 'consentCheck1', bindKey: 'consentCheck1', pageIndex: 0, x: 0.8, y: 0.30, w: 0.05, h: 0.03, fontSize: 16, align: 'center', valueType: 'CHECK_V', value: '' },
    { key: 'consentCheck2', bindKey: 'consentCheck2', pageIndex: 0, x: 0.8, y: 0.35, w: 0.05, h: 0.03, fontSize: 16, align: 'center', valueType: 'CHECK_V', value: '' },
    { key: 'consentCheck3', bindKey: 'consentCheck3', pageIndex: 0, x: 0.8, y: 0.40, w: 0.05, h: 0.03, fontSize: 16, align: 'center', valueType: 'CHECK_V', value: '' },
    { key: 'consentCheck4', bindKey: 'consentCheck4', pageIndex: 0, x: 0.8, y: 0.45, w: 0.05, h: 0.03, fontSize: 16, align: 'center', valueType: 'CHECK_V', value: '' },
    { key: 'consentCheck5', bindKey: 'consentCheck5', pageIndex: 0, x: 0.8, y: 0.50, w: 0.05, h: 0.03, fontSize: 16, align: 'center', valueType: 'CHECK_V', value: '' },
  ]
}

function resolvePreviewText(field, previewValues) {
  const f = field || {}
  const binding = (f.bindKey && String(f.bindKey).trim()) ? String(f.bindKey).trim() : String(f.key || '').trim()
  const vt = String(f.valueType || '').trim().toUpperCase()
  const pv = previewValues || {}
  const { yyyy, mm, dd } = todayParts()
  if (vt === 'TODAY' || binding === 'printedAt') return todayIso()
  if (vt === 'TODAY_YYYY' || binding === 'printedAtYear') return yyyy
  if (vt === 'TODAY_YY' || binding === 'printedAtYear2') return String(yyyy).slice(-2)
  if (vt === 'TODAY_MM' || binding === 'printedAtMonth') return mm
  if (vt === 'TODAY_DD' || binding === 'printedAtDay') return dd
  if (vt === 'CHECK_V' || binding === 'consentCheck') return 'V'
  if (vt === 'SAME_AS_ABOVE') return '상동'
  if (binding === 'actorName' || binding === 'loggedInUserName') {
    return String(pv.actorName || pv.loggedInUserName || '로그인유저')
  }
  if (binding === 'actorPhone' || binding === 'loggedInUserPhone') {
    return String(pv.actorPhone || pv.loggedInUserPhone || '010-0000-0000')
  }
  if (binding === 'actorPhone_1' || binding === 'loggedInUserPhone_1') return '010'
  if (binding === 'actorPhone_2' || binding === 'loggedInUserPhone_2') return '0000'
  if (binding === 'actorPhone_3' || binding === 'loggedInUserPhone_3') return '0000'
  if (binding === 'assignedPlannerLabel') return '담당설계사'
  if (vt === 'STATIC') return String(f.value || '')

  if (binding === 'defaultVCheck') return 'V'
  if (binding === 'defaultSameAsAbove') return '상동'

  if ((vt === 'CHECK_YES' || vt === 'CHECK_NO') && binding) {
    const v = pv[binding]
    const normalized = String(v ?? '').trim().toLowerCase()
    const isYes = normalized === '예' || normalized === 'yes' || normalized === 'true' || normalized === 'y' || normalized === '1' || normalized === '있음'
    const isNo = normalized === '아니오' || normalized === 'no' || normalized === 'false' || normalized === 'n' || normalized === '0' || normalized === '없음'
    if (vt === 'CHECK_YES' && isYes) return 'V'
    if (vt === 'CHECK_NO' && isNo) return 'V'
    return ''
  }

  if (binding.startsWith('accidentType_')) {
    const suffix = binding.slice('accidentType_'.length).trim()
    const at = String(pv.accidentType ?? '').trim()
    if (!suffix) return ''
    if (suffix === '기타') return (at === '기타' || at.startsWith('기타:')) ? 'V' : ''
    return at === suffix ? 'V' : ''
  }

  if (binding.startsWith('claimType_')) {
    const suffix = binding.slice('claimType_'.length).trim()
    const ct = String(pv.claimType ?? '').trim()
    if (!suffix || !ct) return ''
    const parts = ct.split(',').map((p) => p.trim()).filter(Boolean)
    if (suffix === '기타입력') {
      return parts.some((p) => p.startsWith('기타:') && p.slice('기타:'.length).trim()) ? 'V' : ''
    }
    if (suffix === '기타') {
      return parts.some((p) => p === '기타' || p.startsWith('기타:')) ? 'V' : ''
    }
    return parts.includes(suffix) ? 'V' : ''
  }

  if (binding && (binding.endsWith('_Y') || binding.endsWith('_N'))) {
    const baseKey = binding.slice(0, -2)
    const baseValue = String(pv[baseKey] ?? '').trim().toLowerCase()
    const isYes = baseValue === '예' || baseValue === 'yes' || baseValue === 'true' || baseValue === 'y' || baseValue === '1' || baseValue === '있음'
    const isNo = baseValue === '아니오' || baseValue === 'no' || baseValue === 'false' || baseValue === 'n' || baseValue === '0' || baseValue === '없음'
    if (binding.endsWith('_Y') && isYes) return 'V'
    if (binding.endsWith('_N') && isNo) return 'V'
    return ''
  }

  if (binding === 'medicalBenefitRecipient') {
    const v = String(pv[binding] ?? '').trim().toLowerCase()
    const isYes = v === '예' || v === 'yes' || v === 'true' || v === 'y' || v === '1' || v === '있음'
    return isYes ? 'V' : ''
  }

  if (binding === 'customerSignature' || binding === 'contractHolderSignature' || binding === 'beneficiarySignature') return '〈서명〉'
  if (/Signature$/i.test(binding) || /Handwriting/i.test(binding)) return '〈이미지〉'

  /** ClaimFormRenderService.applyDisplayFormatting 과 동일: 전체 주민번호 단일 칸은 123456-1234567 */
  const fullResidentBindKeys = new Set([
    'customerResidentNumber',
    'contractHolderResidentNumber',
    'beneficiaryResidentNumber',
    'beneficiaryResidentNo',
  ])
  if (binding && fullResidentBindKeys.has(binding) && pv[binding] != null && String(pv[binding]).trim()) {
    return formatResidentDisplay(pv[binding])
  }

  if (binding && pv[binding] != null && String(pv[binding]).trim()) return String(pv[binding])
  return String(pv.__other || '예시')
}

/** 청구서 템플릿 valueType DIGITS_SPREAD: 박스 너비에 숫자만 균등 간격(양끝 포함)으로 배치 */
function isDigitsSpreadValueType(field) {
  return String(field?.valueType || '').trim().toUpperCase() === 'DIGITS_SPREAD'
}

function PdfBytesViewer({ title, pdfBytes, filenameHint }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // 기본 배율 100%
  const [pageInfo, setPageInfo] = useState({ width: 0, height: 0, scale: 1.0 })
  const [numPages, setNumPages] = useState(0)
  const [pageNum, setPageNum] = useState(1)
  const [pdfDoc, setPdfDoc] = useState(null)

  const canvasId = useMemo(() => `pdf-out-${String(filenameHint || 'out').replace(/[^a-zA-Z0-9_-]/g, '')}`, [filenameHint])

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!pdfBytes) {
        setPdfDoc(null)
        setNumPages(0)
        setPageNum(1)
        return
      }
      setBusy(true)
      setErr('')
      try {
        // pdf.js는 ArrayBuffer를 worker로 transfer하면서 원본을 detached 시킬 수 있다.
        // 동일 버퍼를 재사용(모달 재오픈 등)하면 DataCloneError가 발생하므로 항상 복사본을 사용한다.
        const bytes = (pdfBytes instanceof ArrayBuffer) ? pdfBytes.slice(0) : pdfBytes
        const { loadingTask, pdf } = await getDocumentWithWorkerFallback({ data: bytes })
        if (cancelled) {
          try { loadingTask.destroy?.() } catch { }
          try { pdf.destroy?.() } catch { }
          return
        }
        setPdfDoc(pdf)
        setNumPages(pdf.numPages || 0)
        setPageNum(1)
      } catch (e) {
        console.error(e)
        if (!cancelled) setErr('미리보기 PDF를 열 수 없습니다.')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfBytes])

  useEffect(() => {
    return () => {
      try { pdfDoc?.destroy?.() } catch { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc])

  useEffect(() => {
    let cancelled = false
    async function render() {
      if (!pdfDoc) return
      setBusy(true)
      setErr('')
      try {
        const n = pdfDoc.numPages || 0
        const safe = Math.max(1, Math.min(n || 1, pageNum || 1))
        if (safe !== pageNum) setPageNum(safe)
        const page = await pdfDoc.getPage(safe)
        const viewport = page.getViewport({ scale: pageInfo.scale || 1.0 })
        if (cancelled) return
        const canvas = document.getElementById(canvasId)
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        canvas.width = Math.floor(viewport.width)
        canvas.height = Math.floor(viewport.height)
        setPageInfo((p) => ({ ...p, width: canvas.width, height: canvas.height }))
        // 한화생명 등 일부 PDF에서 텍스트가 보이지 않는 문제 해결을 위한 렌더링 옵션
        const renderOptions = {
          canvasContext: ctx,
          viewport: viewport,
          enableWebGL: false,
          annotationMode: 0, // DISABLE - renderInteractiveForms 대신 사용
        }
        const task = page.render(renderOptions)
        await task.promise
      } catch (e) {
        console.error(e)
        if (!cancelled) setErr('미리보기 렌더링에 실패했습니다.')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    render()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, pageNum, pageInfo.scale, canvasId])

  return (
    <div className="rounded border p-3 space-y-2 bg-white">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">{title}</div>
        <div className="text-xs text-slate-500">{busy ? '렌더링 중…' : (pageInfo.width ? `${pageInfo.width}×${pageInfo.height}px` : '')}</div>
      </div>
      {err && <div className="text-xs text-rose-600">{err}</div>}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button type="button" className="rounded border px-2 py-1 text-sm disabled:opacity-50" disabled={!numPages || pageNum <= 1 || busy} onClick={() => setPageNum(p => Math.max(1, (p || 1) - 1))}>이전</button>
        <div className="text-sm">
          <input className="w-14 rounded border px-2 py-1 text-sm" value={pageNum} onChange={(e) => setPageNum(Number(e.target.value || 1))} disabled={busy || !numPages} /> / {numPages || '—'}
        </div>
        <button type="button" className="rounded border px-2 py-1 text-sm disabled:opacity-50" disabled={!numPages || pageNum >= numPages || busy} onClick={() => setPageNum(p => Math.min(numPages || 1, (p || 1) + 1))}>다음</button>
        <span className="text-xs text-slate-500">확대</span>
        <select className="rounded border px-2 py-1 text-sm" value={pageInfo.scale} onChange={(e) => setPageInfo((p) => ({ ...p, scale: Number(e.target.value) }))} disabled={busy}>
          <option value={1.0}>100%</option>
          <option value={1.25}>125%</option>
          <option value={1.5}>150%</option>
          <option value={2.0}>200%</option>
        </select>
      </div>
      <div className="rounded border overflow-auto">
        <canvas id={canvasId} className="block" />
      </div>
    </div>
  )
}

function filenameFromContentDisposition(cd) {
  if (!cd || typeof cd !== 'string') return ''
  const filenameStar = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)
  const filenamePlain = cd.match(/filename="([^"]+)"/)
  if (filenameStar?.[1]) {
    try { return decodeURIComponent(filenameStar[1]) } catch { return filenameStar[1] }
  }
  if (filenamePlain?.[1]) return filenamePlain[1]
  return ''
}

// NOTE: CustomersPage 내부에 컴포넌트를 정의하면 상위 state 변경 시 리마운트되어(함수 identity 변경)
// PDF 페이지가 1로 초기화되는 문제가 발생한다. 반드시 파일 스코프로 분리해 상태를 유지한다.
export function PdfTemplateMapper({
  fileId,
  fields,
  selectedKey,
  onSelectKey,
  onSetBox,
  onSetBoxes,
  onQuickCheckAdd,
  previewEnabled,
  previewValues,
  onPageIndexChange,
  fieldLabels,
  onDeleteField,
  enableBulkTools = false,
  /** Set<string> — 보험금 청구 플로우와 연동되는 bindKey 박스를 청록색으로 표시 */
  claimFlowHighlightBindKeys = null,
  /** false면 PDF 오버레이 상단의 매핑 라벨(표시명/bindKey) 배지를 숨김 — 박스·삭제 버튼은 유지 */
  showMappingOverlayLabels = true,
  /** 보험금 청구: 템플릿 기본 PDF 폰트(필드 fontKey 미지정 시 미리보기 글꼴) */
  templateDefaultFontKey = '',
}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  // 기본 배율 100%
  const [pageInfo, setPageInfo] = useState({ width: 0, height: 0, scale: 1.0 })
  const [numPages, setNumPages] = useState(0)
  const [pageNum, setPageNum] = useState(1) // 1-based
  const [pdfBytes, setPdfBytes] = useState(null) // ArrayBuffer
  const [pdfDoc, setPdfDoc] = useState(null) // PDFDocumentProxy
  const [drag, setDrag] = useState(null) // { startX, startY, curX, curY }
  const [moving, setMoving] = useState(null) // { key, startX, startY, originX, originY, wRatio, hRatio, pageIndex, boxWpx, boxHpx, pageW, pageH, curXRatio, curYRatio }
  const [selectedKeys, setSelectedKeys] = useState(() => new Set())

  const canvasId = useMemo(() => `pdf-canvas-${String(fileId || '').replace(/[^a-zA-Z0-9_-]/g, '')}`, [fileId])

  useEffect(() => {
    let cancelled = false
    async function loadPdf() {
      if (!fileId) return
      setBusy(true)
      setErr('')
      try {
        const normalizedFileId = canonicalStoredFileIdForApiPath(fileId)
        const res = await api.get(`/files/${encodeURIComponent(normalizedFileId)}/preview`, { responseType: 'arraybuffer' })
        if (cancelled) return
        // pdf.js getDocument가 buffer를 detach할 수 있으므로 복사본 저장
        setPdfBytes(res.data ? res.data.slice(0) : res.data)
        // fileId가 바뀐 경우에만 1페이지로 초기화
        setPageNum(1)
      } catch (e) {
        console.error(e)
        if (!cancelled) setErr('PDF를 렌더링하지 못했습니다. (파일ID/권한/형식을 확인해주세요)')
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    setPdfBytes(null)
    setPdfDoc(null)
    setNumPages(0)
    setPageNum(1)
    loadPdf()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, canvasId])

  // PDF 문서는 한 번만 로드해서 캐시한다 (페이지 이동 시 재사용)
  useEffect(() => {
    let cancelled = false
    async function loadDoc() {
      if (!pdfBytes) {
        setPdfDoc(null)
        setNumPages(0)
        return
      }
      setBusy(true)
      setErr('')
      try {
        const { loadingTask, pdf } = await getDocumentWithWorkerFallback({ data: pdfBytes })
        if (cancelled) {
          try { loadingTask.destroy?.() } catch { }
          try { pdf.destroy?.() } catch { }
          return
        }
        setPdfDoc(pdf)
        setNumPages(pdf.numPages || 0)
      } catch (e) {
        console.error(e)
        if (!cancelled) {
          const msg = e?.message ? ` (${e.message})` : ''
          setErr(`PDF를 렌더링하지 못했습니다.${msg}`)
        }
        setPdfDoc(null)
        setNumPages(0)
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    loadDoc()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfBytes])

  // pdfDoc가 교체될 때 이전 문서 정리
  useEffect(() => {
    return () => {
      try { pdfDoc?.destroy?.() } catch { }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc])

  // 선택된 필드가 특정 페이지에 있으면 자동으로 그 페이지로 이동
  useEffect(() => {
    if (!selectedKey) return
    const f = (fields || []).find(x => x?.key === selectedKey)
    if (!f) return
    const pi = Number(f.pageIndex ?? 0)
    const next = Math.max(1, pi + 1)
    if (next !== pageNum) setPageNum(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey])

  useEffect(() => {
    if (!selectedKey) return
    setSelectedKeys((prev) => {
      if (prev.has(selectedKey)) return prev
      const next = new Set(prev)
      next.add(selectedKey)
      return next
    })
  }, [selectedKey])

  useEffect(() => {
    const valid = new Set((fields || []).map((f) => f?.key).filter(Boolean))
    setSelectedKeys((prev) => {
      const next = new Set([...prev].filter((key) => valid.has(key)))
      return next.size === prev.size ? prev : next
    })
  }, [fields])

  // 현재 페이지 index를 상위로 전달(필드 추가 시 현재 페이지에 생성)
  useEffect(() => {
    try {
      onPageIndexChange && onPageIndexChange(Math.max(0, (pageNum || 1) - 1), { pageNum, numPages })
    } catch { }
  }, [pageNum, numPages, onPageIndexChange])

  useEffect(() => {
    let cancelled = false
    async function renderPage() {
      if (!fileId || !pdfDoc) return
      setBusy(true)
      setErr('')
      try {
        const n = pdfDoc.numPages || 0
        const safePageNum = Math.max(1, Math.min(n || 1, pageNum || 1))
        if (safePageNum !== pageNum) setPageNum(safePageNum)

        const page = await pdfDoc.getPage(safePageNum)
        const scale = pageInfo.scale || 1.0
        const viewport = page.getViewport({ scale })
        if (cancelled) return

        const canvas = document.getElementById(canvasId)
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        canvas.width = Math.floor(viewport.width)
        canvas.height = Math.floor(viewport.height)
        setPageInfo((prev) => ({ ...prev, width: canvas.width, height: canvas.height, scale }))

        // Canvas 렌더링 품질 향상 (텍스트 가독성 개선)
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'

        // 한화생명 등 일부 PDF에서 텍스트가 보이지 않는 문제 해결을 위한 렌더링 옵션
        // - enableWebGL: false로 설정하여 안정적인 렌더링
        // - annotationMode: 0 (DISABLE) - 주석 렌더링 비활성화로 성능 향상
        const renderOptions = {
          canvasContext: ctx,
          viewport: viewport,
          enableWebGL: false,
          annotationMode: 0, // DISABLE - renderInteractiveForms 대신 사용
        }
        const task = page.render(renderOptions)
        try {
          await task.promise
          
          // 디버깅: 텍스트 레이어 확인 (한화생명 PDF 문제 진단용)
          if (process.env.NODE_ENV === 'development') {
            try {
              const textContent = await page.getTextContent()
              const textItems = textContent.items || []
              if (textItems.length === 0) {
                console.warn('[PDF 렌더링] 텍스트 레이어가 없습니다. PDF가 이미지 기반일 수 있습니다.')
              } else {
                console.log(`[PDF 렌더링] 텍스트 항목 ${textItems.length}개 발견`)
              }
            } catch (textErr) {
              console.warn('[PDF 렌더링] 텍스트 추출 실패:', textErr)
            }
          }
        } catch (e) {
          if (!cancelled) throw e
        }
      } catch (e) {
        console.error(e)
        if (!cancelled) {
          const msg = e?.message ? ` (${e.message})` : ''
          setErr(`PDF를 렌더링하지 못했습니다.${msg}`)
        }
      } finally {
        if (!cancelled) setBusy(false)
      }
    }
    renderPage()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, pdfDoc, canvasId, pageNum, pageInfo.scale])

  const containerStyle = {
    width: pageInfo.width ? `${pageInfo.width}px` : '100%',
    height: pageInfo.height ? `${pageInfo.height}px` : '320px',
  }

  const getPos = (e) => {
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()
    const clientX = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX
    const clientY = (e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
    const y = Math.max(0, Math.min(rect.height, clientY - rect.top))
    return { x, y, w: rect.width, h: rect.height }
  }

  const onPointerDown = (e) => {
    if (moving) return
    if (e.button != null && e.button !== 0) return
    if (!selectedKey) return
    e.preventDefault()
    const p = getPos(e)
    setDrag({ startX: p.x, startY: p.y, curX: p.x, curY: p.y, w: p.w, h: p.h })
  }
  const onPointerMove = (e) => {
    if (moving) return
    if (!drag) return
    e.preventDefault()
    const p = getPos(e)
    setDrag((prev) => prev ? ({ ...prev, curX: p.x, curY: p.y }) : prev)
  }
  const onPointerUp = (e) => {
    if (moving) return
    if (!drag) return
    e.preventDefault()
    const { startX, startY, curX, curY, w, h } = drag
    const pageIndex = Math.max(0, (pageNum || 1) - 1)

    const dx = Math.abs(curX - startX)
    const dy = Math.abs(curY - startY)
    const clickish = (dx < 4 && dy < 4)
    if (clickish) {
      const existing =
        (fields || []).find(ff => ff?.key === selectedKey && Number(ff?.pageIndex ?? 0) === pageIndex)
        || (fields || []).find(ff => ff?.key === selectedKey)
      const keepW = Number(existing?.w ?? 0.2)
      const keepH = Number(existing?.h ?? 0.03)
      const rx = startX / w
      const ry = startY / h
      onSetBox && onSetBox(selectedKey, { pageIndex, x: rx, y: ry, w: keepW, h: keepH })
      setDrag(null)
      return
    }

    const x1 = Math.min(startX, curX)
    const y1 = Math.min(startY, curY)
    const x2 = Math.max(startX, curX)
    const y2 = Math.max(startY, curY)
    const bw = Math.max(1, x2 - x1)
    const bh = Math.max(1, y2 - y1)
    const rx = x1 / w
    const ry = y1 / h
    const rw = bw / w
    const rh = bh / h
    onSetBox && onSetBox(selectedKey, { pageIndex, x: rx, y: ry, w: rw, h: rh })
    setDrag(null)
  }

  const onContextMenu = (e) => {
    if (!onQuickCheckAdd || !pageInfo.width || !pageInfo.height) return
    e.preventDefault()
    e.stopPropagation()
    const p = getPos(e)
    onQuickCheckAdd({
      pageIndex: Math.max(0, (pageNum || 1) - 1),
      x: p.x / p.w,
      y: p.y / p.h,
      pageWidth: p.w,
      pageHeight: p.h,
    })
  }

  const currentPageIndex = Math.max(0, (pageNum || 1) - 1)
  const currentPageFields = useMemo(() => (
    (fields || [])
      .filter((f) =>
        f?.key
        && Number(f?.pageIndex ?? 0) === currentPageIndex
        && Number(f?.w || 0) > 0
        && Number(f?.h || 0) > 0
      )
  ), [fields, currentPageIndex])
  const selectedPageFields = useMemo(() => (
    currentPageFields.filter((f) => selectedKeys.has(f.key))
  ), [currentPageFields, selectedKeys])

  const commitBoxes = (updates) => {
    if (!Array.isArray(updates) || updates.length === 0) return
    if (onSetBoxes) {
      onSetBoxes(updates)
      return
    }
    updates.forEach(({ key, box }) => {
      if (key) onSetBox && onSetBox(key, box)
    })
  }

  const applyBulkUpdate = (buildPatch) => {
    const rows = selectedPageFields
    if (rows.length < 2) return
    const anchor = rows.find((f) => f.key === selectedKey) || rows[0]
    const updates = rows
      .map((f) => {
        const patch = buildPatch(f, anchor, rows)
        return patch ? { key: f.key, box: { ...f, ...patch } } : null
      })
      .filter(Boolean)
    commitBoxes(updates)
  }

  const distributeSelected = (axis) => {
    const rows = [...selectedPageFields].sort((a, b) => Number(a?.[axis] ?? 0) - Number(b?.[axis] ?? 0))
    if (rows.length < 3) return
    const min = Number(rows[0]?.[axis] ?? 0)
    const max = Number(rows[rows.length - 1]?.[axis] ?? min)
    const step = (max - min) / (rows.length - 1)
    commitBoxes(rows.map((f, idx) => ({
      key: f.key,
      box: { ...f, [axis]: min + step * idx },
    })))
  }

  const selectAllCurrentPage = () => {
    const keys = currentPageFields.map((f) => f.key).filter(Boolean)
    setSelectedKeys(new Set(keys))
    if (keys.length > 0) onSelectKey && onSelectKey(keys[keys.length - 1])
  }

  // 박스 이동(드래그) 처리 (이동 중에는 내부 preview만 업데이트, mouseup에 1회 커밋)
  useEffect(() => {
    if (!moving) return
    function onMove(ev) {
      try {
        const isTouch = ev.touches && ev.touches[0]
        const clientX = isTouch ? ev.touches[0].clientX : ev.clientX
        const clientY = isTouch ? ev.touches[0].clientY : ev.clientY
        const dx = clientX - moving.startX
        const dy = clientY - moving.startY
        const pageW = moving.pageW || pageInfo.width || 1
        const pageH = moving.pageH || pageInfo.height || 1
        const maxX = Math.max(0, pageW - (moving.boxWpx || 0))
        const maxY = Math.max(0, pageH - (moving.boxHpx || 0))
        const nxPx = Math.max(0, Math.min(maxX, (moving.originX || 0) + dx))
        const nyPx = Math.max(0, Math.min(maxY, (moving.originY || 0) + dy))
        const nx = nxPx / pageW
        const ny = nyPx / pageH
        setMoving((prev) => prev ? ({ ...prev, curXRatio: nx, curYRatio: ny }) : prev)
      } catch {
      }
    }
    function onUp() {
      try {
        const pageW = moving.pageW || pageInfo.width || 1
        const pageH = moving.pageH || pageInfo.height || 1
        const x = moving.curXRatio != null ? moving.curXRatio : Number(moving.originX || 0) / pageW
        const y = moving.curYRatio != null ? moving.curYRatio : Number(moving.originY || 0) / pageH
        onSetBox && onSetBox(moving.key, {
          pageIndex: moving.pageIndex,
          x,
          y,
          w: moving.wRatio,
          h: moving.hRatio,
        })
      } catch { }
      setMoving(null)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moving, pageInfo.width, pageInfo.height, onSetBox])

  const boxStyle = (f) => {
    const fPageIndex = Number(f?.pageIndex ?? 0)
    const overrideX = (moving && moving.key === f?.key && moving.pageIndex === fPageIndex && moving.curXRatio != null)
      ? moving.curXRatio
      : Number(f?.x ?? 0)
    const overrideY = (moving && moving.key === f?.key && moving.pageIndex === fPageIndex && moving.curYRatio != null)
      ? moving.curYRatio
      : Number(f?.y ?? 0)
    const px = overrideX * pageInfo.width
    const py = overrideY * pageInfo.height
    const pw = Number(f?.w ?? 0) * pageInfo.width
    const ph = Number(f?.h ?? 0) * pageInfo.height
    return {
      left: `${px}px`,
      top: `${py}px`,
      width: `${pw}px`,
      height: `${ph}px`,
    }
  }

  const textStyle = (f) => {
    const s = boxStyle(f)
    const fPageIndex = Number(f?.pageIndex ?? 0)
    const overrideX = (moving && moving.key === f?.key && moving.pageIndex === fPageIndex && moving.curXRatio != null)
      ? moving.curXRatio
      : Number(f?.x ?? 0)
    const overrideY = (moving && moving.key === f?.key && moving.pageIndex === fPageIndex && moving.curYRatio != null)
      ? moving.curYRatio
      : Number(f?.y ?? 0)
    const pw = Number(f?.w ?? 0) * pageInfo.width
    const ph = Number(f?.h ?? 0) * pageInfo.height
    const scaleAdj = (pageInfo.scale || 1.0) / 1.5
    const fsBase = Number(f?.fontSize ?? 12) * scaleAdj
    const fontSizePx = Math.max(8, Math.min(ph * 0.8, fsBase || 12))
    const align = String(f?.align || 'left')
    const justifyContent = align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start')
    return {
      ...s,
      left: `${overrideX * pageInfo.width}px`,
      top: `${overrideY * pageInfo.height}px`,
      width: `${pw}px`,
      height: `${ph}px`,
      fontSize: `${fontSizePx}px`,
      justifyContent,
      fontFamily: previewFontFamilyForMappingKey(f?.fontKey || templateDefaultFontKey),
    }
  }

  const dragRect = (() => {
    if (!drag) return null
    const x1 = Math.min(drag.startX, drag.curX)
    const y1 = Math.min(drag.startY, drag.curY)
    const x2 = Math.max(drag.startX, drag.curX)
    const y2 = Math.max(drag.startY, drag.curY)
    return { left: x1, top: y1, width: Math.max(1, x2 - x1), height: Math.max(1, y2 - y1) }
  })()

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">PDF 미리보기 — 드래그로 좌표 지정</div>
        <div className="text-xs text-slate-500">{busy ? '렌더링 중…' : (pageInfo.width ? `${pageInfo.width}×${pageInfo.height}px` : '')}</div>
      </div>
      {err && <div className="text-xs text-red-500">{err}</div>}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded border px-2 py-1 text-sm disabled:opacity-50"
            disabled={!numPages || pageNum <= 1 || busy}
            onClick={() => setPageNum((p) => Math.max(1, (p || 1) - 1))}
          >
            이전
          </button>
          <div className="text-sm">
            페이지{' '}
            <input
              className="w-14 rounded border px-2 py-1 text-sm"
              value={pageNum}
              onChange={(e) => setPageNum(Number(e.target.value || 1))}
              onBlur={() => setPageNum((p) => Math.max(1, Math.min(numPages || 1, Number(p || 1))))}
              disabled={busy || !numPages}
            />{' '}
            / {numPages || '—'}
          </div>
          <button
            type="button"
            className="rounded border px-2 py-1 text-sm disabled:opacity-50"
            disabled={!numPages || pageNum >= numPages || busy}
            onClick={() => setPageNum((p) => Math.min(numPages || 1, (p || 1) + 1))}
          >
            다음
          </button>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">확대</span>
          <select
            className="rounded border px-2 py-1 text-sm"
            value={pageInfo.scale}
            onChange={(e) => setPageInfo((prev) => ({ ...prev, scale: Number(e.target.value) }))}
            disabled={busy}
          >
            <option value={1.0}>100%</option>
            <option value={1.25}>125%</option>
            <option value={1.5}>150%</option>
            <option value={2.0}>200%</option>
          </select>
        </div>

        <div className="text-xs text-slate-500">
          현재 페이지 index: {Math.max(0, (pageNum || 1) - 1)}
        </div>
      </div>

      {enableBulkTools && (
        <div className="rounded border bg-slate-50 p-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold text-slate-700">멀티 정렬</span>
            <span className="text-slate-500">Ctrl/Shift 클릭으로 여러 필드를 선택합니다. 선택 {selectedPageFields.length}개</span>
            <button type="button" className="rounded border bg-white px-2 py-1 hover:bg-slate-100" onClick={selectAllCurrentPage}>
              현재 페이지 전체 선택
            </button>
            <button type="button" className="rounded border bg-white px-2 py-1 hover:bg-slate-100" onClick={() => setSelectedKeys(new Set())}>
              선택 해제
            </button>
            <button type="button" disabled={selectedPageFields.length < 2} className="rounded border bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50" onClick={() => applyBulkUpdate((_, anchor) => ({ x: Number(anchor.x ?? 0) }))}>
              왼쪽 정렬
            </button>
            <button type="button" disabled={selectedPageFields.length < 2} className="rounded border bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50" onClick={() => applyBulkUpdate((_, anchor) => ({ y: Number(anchor.y ?? 0) }))}>
              위쪽 정렬
            </button>
            <button type="button" disabled={selectedPageFields.length < 2} className="rounded border bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50" onClick={() => applyBulkUpdate((_, anchor) => ({ w: Number(anchor.w ?? 0), h: Number(anchor.h ?? 0) }))}>
              동일 크기
            </button>
            <button type="button" disabled={selectedPageFields.length < 2} className="rounded border bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50" onClick={() => applyBulkUpdate((_, anchor) => ({ w: Number(anchor.w ?? 0) }))}>
              가로 동일 크기
            </button>
            <button type="button" disabled={selectedPageFields.length < 2} className="rounded border bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50" onClick={() => applyBulkUpdate((_, anchor) => ({ h: Number(anchor.h ?? 0) }))}>
              세로 동일 크기
            </button>
            <button type="button" disabled={selectedPageFields.length < 3} className="rounded border bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50" onClick={() => distributeSelected('y')}>
              세로 간격 균등
            </button>
            <button type="button" disabled={selectedPageFields.length < 3} className="rounded border bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-50" onClick={() => distributeSelected('x')}>
              가로 간격 균등
            </button>
          </div>
        </div>
      )}

      <div className="rounded border bg-white overflow-auto">
        <div className="relative select-none" style={containerStyle}>
          <canvas id={canvasId} className="absolute left-0 top-0" />
          {/* overlay */}
          <div
            className="absolute left-0 top-0"
            style={{ width: `${pageInfo.width}px`, height: `${pageInfo.height}px` }}
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={onPointerUp}
            onMouseLeave={onPointerUp}
            onContextMenu={onContextMenu}
            onTouchStart={onPointerDown}
            onTouchMove={onPointerMove}
            onTouchEnd={onPointerUp}
          >
            {currentPageFields
              .map((f) => {
                const active = f.key === selectedKey || selectedKeys.has(f.key)
                const flowHL =
                  claimFlowHighlightBindKeys && f.bindKey && claimFlowHighlightBindKeys.has(String(f.bindKey).trim())
                // 같은 bindKey를 가진 필드들 중에서 현재 필드의 순서 찾기 (전체 필드 배열에서)
                const sameBindKeyFields = (fields || []).filter(ff => ff.bindKey === f.bindKey)
                const fieldIndex = sameBindKeyFields.findIndex(ff => ff.key === f.key)
                const fieldNumber = sameBindKeyFields.length > 1 ? fieldIndex + 1 : null
                
                // 한글 필드명 가져오기
                const koreanLabel = (fieldLabels && f.bindKey) ? (fieldLabels[f.bindKey] || f.bindKey) : f.key
                const displayLabel = fieldNumber ? `${koreanLabel}_${fieldNumber}` : koreanLabel
                
                const boxTone = active
                  ? 'border-brand-sky bg-brand-sky/10'
                  : flowHL
                    ? 'border-cyan-600 bg-cyan-500/15'
                    : 'border-amber-400 bg-amber-400/10'
                const labelTone = active
                  ? 'bg-brand-sky text-white'
                  : flowHL
                    ? 'bg-cyan-700 text-white'
                    : 'bg-amber-400 text-slate-900'
                
                return (
                  <div
                    key={`${f.key}-${Number(f?.pageIndex ?? 0)}`}
                    className={`absolute border cursor-move ${boxTone}`}
                    style={boxStyle(f)}
                    onClick={(e) => {
                      e.stopPropagation()
                      const next = Math.max(1, Number(f.pageIndex ?? 0) + 1)
                      if (next !== pageNum) setPageNum(next)
                      if (enableBulkTools && (e.ctrlKey || e.metaKey || e.shiftKey)) {
                        const nextSet = new Set(selectedKeys)
                        const wasSelected = nextSet.has(f.key)
                        if (wasSelected) nextSet.delete(f.key)
                        else nextSet.add(f.key)
                        setSelectedKeys(nextSet)
                        if (!wasSelected) {
                          onSelectKey && onSelectKey(f.key)
                        } else if (selectedKey === f.key) {
                          const fallback = [...nextSet][nextSet.size - 1] || null
                          onSelectKey && onSelectKey(fallback)
                        }
                        return
                      } else if (enableBulkTools) {
                        setSelectedKeys(new Set([f.key]))
                      }
                      onSelectKey && onSelectKey(f.key)
                    }}
                    onMouseDown={(e) => {
                      // 박스 이동 시작 (새 박스 그리기 방지)
                      e.stopPropagation()
                      if (e.button !== 0) return
                      if (enableBulkTools && (e.ctrlKey || e.metaKey || e.shiftKey)) return
                      const rect = e.currentTarget.parentElement?.getBoundingClientRect()
                      const clientX = e.clientX
                      const clientY = e.clientY
                      const pageW = pageInfo.width || rect?.width || 1
                      const pageH = pageInfo.height || rect?.height || 1
                      const originX = Number(f?.x ?? 0) * pageW
                      const originY = Number(f?.y ?? 0) * pageH
                      const boxWpx = Number(f?.w ?? 0) * pageW
                      const boxHpx = Number(f?.h ?? 0) * pageH
                      setMoving({
                        key: f.key,
                        startX: clientX,
                        startY: clientY,
                        originX,
                        originY,
                        wRatio: Number(f?.w ?? 0),
                        hRatio: Number(f?.h ?? 0),
                        pageIndex: Number(f?.pageIndex ?? 0),
                        boxWpx,
                        boxHpx,
                        pageW,
                        pageH,
                      })
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation()
                      const t = e.touches?.[0]
                      if (!t) return
                      const rect = e.currentTarget.parentElement?.getBoundingClientRect()
                      const pageW = pageInfo.width || rect?.width || 1
                      const pageH = pageInfo.height || rect?.height || 1
                      const originX = Number(f?.x ?? 0) * pageW
                      const originY = Number(f?.y ?? 0) * pageH
                      const boxWpx = Number(f?.w ?? 0) * pageW
                      const boxHpx = Number(f?.h ?? 0) * pageH
                      setMoving({
                        key: f.key,
                        startX: t.clientX,
                        startY: t.clientY,
                        originX,
                        originY,
                        wRatio: Number(f?.w ?? 0),
                        hRatio: Number(f?.h ?? 0),
                        pageIndex: Number(f?.pageIndex ?? 0),
                        boxWpx,
                        boxHpx,
                        pageW,
                        pageH,
                      })
                    }}
                    title={`${displayLabel} (${f.bindKey || f.key})`}
                  >
                    {showMappingOverlayLabels && (
                    <span 
                      className={`absolute -top-5 left-0 text-[11px] px-1 rounded max-w-[200px] truncate block ${labelTone}`}
                      title={displayLabel}
                    >
                      {displayLabel}
                    </span>
                    )}
                    {onDeleteField && (
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold hover:bg-red-600 flex items-center justify-center shadow-md z-10"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (window.confirm('이 필드를 삭제하시겠습니까?')) {
                            onDeleteField(f.key)
                          }
                        }}
                        title="필드 삭제"
                      >
                        ×
                      </button>
                    )}
                  </div>
                )
              })}

            {/* preview values overlay (임의값) */}
            {previewEnabled && (
              <div className="absolute left-0 top-0 pointer-events-none" style={{ width: `${pageInfo.width}px`, height: `${pageInfo.height}px` }}>
                {(fields || [])
                  .filter(f =>
                    f?.key
                    && (Number(f?.pageIndex ?? 0) === Math.max(0, (pageNum || 1) - 1))
                    && (Number(f?.w || 0) > 0)
                    && (Number(f?.h || 0) > 0)
                  )
                  .map((f, idx) => {
                    const isImage = String(f?.type || '').toLowerCase() === 'image'
                    if (isImage) {
                      return (
                        <div
                          key={`${f.key}-${idx}`}
                          className="absolute flex items-center justify-center border border-sky-300 bg-sky-100/50 text-xs font-semibold text-sky-800"
                          style={{
                            ...boxStyle(f),
                            paddingLeft: '4px',
                            paddingRight: '4px',
                            overflow: 'hidden',
                          }}
                          title={f.value || '이미지'}
                        >
                          이미지
                        </div>
                      )
                    }
                    const text = resolvePreviewText(f, previewValues)
                    if (!text) return null
                    const spread = isDigitsSpreadValueType(f)
                    const digitChars = spread ? String(text).replace(/\D/g, '') : ''
                    const useDigitSpread = spread && digitChars.length > 0
                    const ts = textStyle(f)
                    return (
                      <div
                        key={`${f.key}-${idx}`}
                        className="absolute text-slate-900/80"
                        style={{
                          ...ts,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: useDigitSpread ? 'space-evenly' : ts.justifyContent,
                          paddingLeft: '4px',
                          paddingRight: '4px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          width: ts.width,
                          minWidth: 0,
                        }}
                        title={text}
                      >
                        {useDigitSpread
                          ? digitChars.split('').map((c, i) => (
                            <span key={i} className="inline-block shrink-0">{c}</span>
                          ))
                          : text}
                      </div>
                    )
                  })}
              </div>
            )}

            {dragRect && (
              <div
                className="absolute border-2 border-red-500 bg-red-500/10 pointer-events-none"
                style={{ left: `${dragRect.left}px`, top: `${dragRect.top}px`, width: `${dragRect.width}px`, height: `${dragRect.height}px` }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function CustomersPage() {
  const { user } = useAuth()
  const { show } = useToast()
  const location = useLocation()
  const navigate = useNavigate()

  const [activeTab, setActiveTab] = useState('consent')
  const [orgOptions, setOrgOptions] = useState([])
  const [loadingOrgs, setLoadingOrgs] = useState(false)

  const [form, setForm] = useState({
    customerName: '',
    birthDate: '',
    phoneNumber: '',
  })
  const [downloading, setDownloading] = useState(false)
  const [selectedInsurerOrgIds, setSelectedInsurerOrgIds] = useState([]) // string[]
  const [issuedAvailableOrgIds, setIssuedAvailableOrgIds] = useState(new Set()) // Set<string>
  const [issuedStatusByOrgId, setIssuedStatusByOrgId] = useState({}) // { [orgId: string]: { updatedAt?: string, pageCount?: number } }
  const [templatesByInsurer, setTemplatesByInsurer] = useState({}) // { [orgId: string]: { fields: FieldItem[], raw: any } }
  const [templateAvailableOrgIds, setTemplateAvailableOrgIds] = useState(new Set()) // Set<string> (활성 템플릿 있는 보험사)
  const [templateStatusByOrgId, setTemplateStatusByOrgId] = useState({}) // { [orgId: string]: { hasTemplate: boolean, updatedAt?: string, expectedPageCount?: number, reason?: string } }
  const [noTemplateModalOpen, setNoTemplateModalOpen] = useState(false)
  const [showNoTemplateInUploadList, setShowNoTemplateInUploadList] = useState(false) // 업로드 탭 보험사 목록에서 "템플릿 없음"도 보여줄지
  // 동의서 출력 입력값은 보험사별이 아니라 bindKey 기준으로 1번만 입력(겹치면 공유)
  const [extraInputs, setExtraInputs] = useState({}) // { [bindKey: string]: string }
  const [previewInsurerOrgId, setPreviewInsurerOrgId] = useState('') // string
  const [previewing, setPreviewing] = useState(false)
  const [previewPdfBytesByOrgId, setPreviewPdfBytesByOrgId] = useState({}) // { [orgId: string]: ArrayBuffer }
  const [previewFilenameHint, setPreviewFilenameHint] = useState('')
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewModalErr, setPreviewModalErr] = useState('')
  const [downloadingOneInModal, setDownloadingOneInModal] = useState(false)
  const [downloadingAllInModal, setDownloadingAllInModal] = useState(false)

  const isOperator = useMemo(() => (user?.roles || []).includes(Roles.OPERATOR), [user?.roles])
  const isConsentUser = Boolean(user)

  const [uploadInsurerOrgId, setUploadInsurerOrgId] = useState('') // string
  const [uploadingIssuedFromUploadPage, setUploadingIssuedFromUploadPage] = useState(false)
  const [uploadLayoutCheck, setUploadLayoutCheck] = useState({ status: 'idle', message: '', details: null }) // {status, message, details}
  const goTab = (t) => {
    const allowed = new Set(['consent', 'upload', 'setup'])
    const next = allowed.has(t) ? t : 'consent'
    if (next === 'setup' && !isOperator) {
      setActiveTab('consent')
      navigate('/customers?tab=consent', { replace: true })
      return
    }
    setActiveTab(next)
    navigate(`/customers?tab=${encodeURIComponent(next)}`, { replace: true })
  }

  // URL query (?tab=...)로 탭 진입 제어
  useEffect(() => {
    const qs = new URLSearchParams(location.search || '')
    const t = qs.get('tab')
    if (!t) return
    if (t === 'setup' && !isOperator) {
      if (activeTab !== 'consent') setActiveTab('consent')
      return
    }
    if (['consent', 'setup', 'upload'].includes(t) && t !== activeTab) {
      setActiveTab(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isOperator])

  // 동의서 템플릿/발행본 세팅(MVP: 수동 좌표 입력)
  const [setup, setSetup] = useState({
    insurerOrgId: '',
    templateFileId: '',
    issuedFileId: '',
    fields: defaultConsentTemplateFields(),
  })
  const [uploadingTemplate, setUploadingTemplate] = useState(false)
  const [uploadingIssued, setUploadingIssued] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [savingIssued, setSavingIssued] = useState(false)
  const [selectedFieldKey, setSelectedFieldKey] = useState('customerName')
  const [setupPreviewEnabled, setSetupPreviewEnabled] = useState(true)
  const [setupPreviewValues, setSetupPreviewValues] = useState({
    customerName: '홍길동',
    actorName: user?.name || '',
    __other: '예시',
  })
  const [setupCurrentPageIndex, setSetupCurrentPageIndex] = useState(0) // PdfTemplateMapper의 현재 페이지(0-based)

  useEffect(() => {
    let ignore = false
    async function loadOrgs() {
      setLoadingOrgs(true)
      try {
        const scope = isOperator ? 'all' : undefined
        const res = await api.get('/orgs/options', { params: scope ? { scope } : undefined })
        const payload = Array.isArray(res.data?.data) ? res.data.data : (Array.isArray(res.data) ? res.data : [])
        if (!ignore) setOrgOptions(Array.isArray(payload) ? payload : [])
      } catch (e) {
        if (!ignore) setOrgOptions([])
        show('보험사 목록을 불러오지 못했습니다.', 'error')
      } finally {
        if (!ignore) setLoadingOrgs(false)
      }
    }
    loadOrgs()
    return () => { ignore = true }
  }, [isOperator, show])

  // 생보/손보 카테고리 루트 id를 동적으로 찾는다 (seed id 고정 의존 제거)
  const lifeRootId = useMemo(() => {
    const root = orgOptions.find(o => o?.type === 'INSURER' && !o.parentOrgId && o.name === '생명보험')
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])
  const nonlifeRootId = useMemo(() => {
    const root = orgOptions.find(o => o?.type === 'INSURER' && !o.parentOrgId && o.name === '손해보험')
    return root?.id != null ? Number(root.id) : null
  }, [orgOptions])

  // 보험사 회사 레벨만 표시: parentOrgId가 생보/손보 루트인 INSURER (삼성생명/삼성화재 등)
  const insurerCompanies = useMemo(() => {
    const list = (orgOptions || []).filter(o =>
      o?.type === 'INSURER'
      && o.parentOrgId
      && (
        (lifeRootId != null && Number(o.parentOrgId) === lifeRootId)
        || (nonlifeRootId != null && Number(o.parentOrgId) === nonlifeRootId)
      )
    )
    return list.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
  }, [orgOptions, lifeRootId, nonlifeRootId])

  const lifeInsurers = useMemo(() => insurerCompanies.filter(o => lifeRootId != null && Number(o.parentOrgId) === lifeRootId), [insurerCompanies, lifeRootId])
  const nonlifeInsurers = useMemo(() => insurerCompanies.filter(o => nonlifeRootId != null && Number(o.parentOrgId) === nonlifeRootId), [insurerCompanies, nonlifeRootId])

  // 동의서 출력 탭 보험사 목록: 발행본 업로드(선택 가능) 보험사를 먼저 노출
  const sortInsurersByIssuedFirst = React.useCallback((list) => {
    const arr = Array.isArray(list) ? [...list] : []
    arr.sort((a, b) => {
      const aAvail = issuedAvailableOrgIds.has(String(a?.id))
      const bAvail = issuedAvailableOrgIds.has(String(b?.id))
      if (aAvail !== bAvail) return aAvail ? -1 : 1
      return String(a?.name || '').localeCompare(String(b?.name || ''))
    })
    return arr
  }, [issuedAvailableOrgIds])

  const lifeInsurersSorted = useMemo(() => sortInsurersByIssuedFirst(lifeInsurers), [lifeInsurers, sortInsurersByIssuedFirst])
  const nonlifeInsurersSorted = useMemo(() => sortInsurersByIssuedFirst(nonlifeInsurers), [nonlifeInsurers, sortInsurersByIssuedFirst])
  const insurerNameById = useMemo(() => {
    const m = new Map()
    for (const o of insurerCompanies) m.set(String(o.id), o.name)
    return m
  }, [insurerCompanies])

  const parseTemplateFields = React.useCallback((fieldsJson) => {
    if (!fieldsJson) return []
    try {
      const parsed = (typeof fieldsJson === 'string') ? JSON.parse(fieldsJson) : fieldsJson
      if (Array.isArray(parsed)) return parsed
      if (parsed && typeof parsed === 'object') {
        return Object.entries(parsed).map(([k, v]) => ({ key: k, bindKey: k, ...(v || {}) }))
      }
      return []
    } catch {
      return []
    }
  }, [])

  // 보험사별 "발행본 업로드 여부"를 bulk로 조회해, 미업로드 보험사는 선택 불가 처리
  useEffect(() => {
    let ignore = false
    async function loadIssuedAvailability() {
      try {
        if (!isConsentUser) return
        const ids = insurerCompanies.map(o => o?.id).filter(Boolean)
        if (ids.length === 0) {
          if (!ignore) setIssuedAvailableOrgIds(new Set())
          if (!ignore) setIssuedStatusByOrgId({})
          return
        }
        const res = await api.get('/consent-forms/issued-pdfs/active-available', { params: { insurerOrgIds: ids.join(',') } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const list = Array.isArray(payload?.availableOrgIds) ? payload.availableOrgIds : (Array.isArray(payload) ? payload : [])
        if (!ignore) setIssuedAvailableOrgIds(new Set(list.map(String)))

        const items = Array.isArray(payload?.items) ? payload.items : []
        if (!ignore) {
          const m = {}
          for (const it of items) {
            const orgId = it?.insurerOrgId != null ? String(it.insurerOrgId) : ''
            if (!orgId) continue
            m[orgId] = { updatedAt: it?.updatedAt, pageCount: it?.pageCount }
          }
          setIssuedStatusByOrgId(m)
        }
      } catch (e) {
        if (!ignore) setIssuedAvailableOrgIds(new Set())
        if (!ignore) setIssuedStatusByOrgId({})
      }
    }
    loadIssuedAvailability()
    return () => { ignore = true }
  }, [isConsentUser, insurerCompanies])

  // 보험사별 "활성 템플릿 존재 여부"를 bulk로 조회해, 업로드 탭에서 템플릿 미설정 보험사를 명확히 표시
  useEffect(() => {
    let ignore = false
    async function loadTemplateAvailability() {
      try {
        if (!isConsentUser) return
        const ids = insurerCompanies.map(o => o?.id).filter(Boolean)
        if (ids.length === 0) {
          if (!ignore) setTemplateAvailableOrgIds(new Set())
          if (!ignore) setTemplateStatusByOrgId({})
          return
        }
        const res = await api.get('/consent-forms/templates/active-available', { params: { insurerOrgIds: ids.join(',') } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const list = Array.isArray(payload?.availableOrgIds) ? payload.availableOrgIds : []
        if (!ignore) setTemplateAvailableOrgIds(new Set(list.map(String)))

        const items = Array.isArray(payload?.items) ? payload.items : []
        if (!ignore) {
          const m = {}
          for (const it of items) {
            const orgId = it?.insurerOrgId != null ? String(it.insurerOrgId) : ''
            if (!orgId) continue
            m[orgId] = {
              hasTemplate: !!it?.hasTemplate,
              updatedAt: it?.updatedAt,
              expectedPageCount: it?.expectedPageCount,
              reason: it?.reason,
            }
          }
          setTemplateStatusByOrgId(m)
        }
      } catch (e) {
        if (!ignore) setTemplateAvailableOrgIds(new Set())
        if (!ignore) setTemplateStatusByOrgId({})
      }
    }
    loadTemplateAvailability()
    return () => { ignore = true }
  }, [isConsentUser, insurerCompanies])

  // 선택 보험사들의 활성 템플릿(필드 정의) 로드 → 동의서 출력 폼에 반영
  useEffect(() => {
    let ignore = false
    async function loadTemplates() {
      if (!isConsentUser) return
      const ids = new Set(Array.isArray(selectedInsurerOrgIds) ? selectedInsurerOrgIds : [])
      if (uploadInsurerOrgId) ids.add(String(uploadInsurerOrgId))
      if (setup?.insurerOrgId) ids.add(String(setup.insurerOrgId))
      // 운영자 좌표설정 탭에서는 보험사 목록에 "템플릿 있음/없음" 상태 표시를 위해 백그라운드로 미리 로드한다.
      if (isOperator && activeTab === 'setup') {
        for (const o of (insurerCompanies || [])) {
          const id = o?.id != null ? String(o.id) : ''
          if (id) ids.add(id)
        }
      }
      for (const id of Array.from(ids)) {
        if (templatesByInsurer[id]) continue
        try {
          const res = await api.get('/consent-forms/templates/active', { params: { insurerOrgId: Number(id) } })
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          const fields = parseTemplateFields(payload?.fieldsJson)
          if (!ignore) {
            setTemplatesByInsurer((prev) => ({ ...prev, [id]: { fields, raw: payload } }))
          }
          // STATIC 기본값은 입력 폼에 프리필(수정 가능)
          if (!ignore) {
            setExtraInputs((prev) => {
              const cur = { ...(prev || {}) }
              for (const f of fields) {
                const binding = (f?.bindKey && String(f.bindKey).trim()) ? String(f.bindKey).trim() : String(f?.key || '').trim()
                if (!binding) continue
                const vt = String(f?.valueType || '').toUpperCase()
                if (vt === 'STATIC' && cur[binding] == null) {
                  cur[binding] = String(f?.value || '')
                }
              }
              return cur
            })
          }
        } catch (e) {
          // 템플릿이 없을 수도 있음(운영자가 아직 등록 전)
          if (!ignore) setTemplatesByInsurer((prev) => ({ ...prev, [id]: { fields: [], raw: null } }))
        }
      }
    }
    loadTemplates()
    return () => { ignore = true }
  }, [selectedInsurerOrgIds, uploadInsurerOrgId, setup?.insurerOrgId, isOperator, activeTab, insurerCompanies, isConsentUser, parseTemplateFields, templatesByInsurer])

  // 운영자 템플릿/좌표 설정: 보험사 선택 시, 기존 활성 템플릿 좌표를 자동 로드
  const lastSetupAutoLoadedInsurerIdRef = React.useRef(null)
  useEffect(() => {
    if (!isOperator || activeTab !== 'setup') return
    const id = String(setup?.insurerOrgId || '').trim()
    if (!id) {
      lastSetupAutoLoadedInsurerIdRef.current = null
      return
    }
    const info = templatesByInsurer[id]
    if (!info) return // 아직 로딩 중
    if (lastSetupAutoLoadedInsurerIdRef.current === id) return
    lastSetupAutoLoadedInsurerIdRef.current = id

    const templateFileId = info?.raw?.templateFileId ? String(info.raw.templateFileId) : ''
    const fields = Array.isArray(info?.fields) ? info.fields : []
    setSetup((prev) => ({
      ...prev,
      templateFileId,
      // 중요: 템플릿이 없으면(또는 필드가 비면) 이전 보험사 좌표가 남지 않도록 기본 필드로 초기화
      fields: fields.length > 0 ? fields : defaultConsentTemplateFields(),
    }))
    // 선택된 필드가 현재 필드에 없으면 첫 필드로 맞춘다.
    try {
      const nextFields = fields.length > 0 ? fields : defaultConsentTemplateFields()
      const nextSelected = (nextFields || []).find(f => f?.key)?.key
      if (nextSelected) setSelectedFieldKey(nextSelected)
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOperator, activeTab, setup?.insurerOrgId, templatesByInsurer])

  const CONSENT_COMMON_BINDINGS = useMemo(() => new Set(['customerName', 'birthDate', 'phoneNumber']), [])
  const CONSENT_AUTO_BINDINGS = useMemo(() => new Set([
    'printedAt',
    'printedAtYear',
    'printedAtYear2',
    'printedAtMonth',
    'printedAtDay',
    'actorName',
    'consentCheck',
    'consentCheck1',
    'consentCheck2',
    'consentCheck3',
    'consentCheck4',
    'consentCheck5',
  ]), [])
  const consentBindingLabel = React.useCallback((binding, fallbackKey) => {
    const b = String(binding || '').trim()
    if (b === 'customerName') return '성명'
    if (b === 'birthDate') return '생년월일'
    if (b === 'phoneNumber') return '연락처'
    if (b === 'printedAt') return '출력일'
    if (b === 'printedAtYear') return '출력년'
    if (b === 'printedAtYear2') return '출력년(2자리)'
    if (b === 'printedAtMonth') return '출력월'
    if (b === 'printedAtDay') return '출력일(일)'
    if (b === 'actorName') return '담당자명'
    if (b === 'consentCheck') return '체크표시'
    if (b === 'consentCheck1') return '체크박스1'
    if (b === 'consentCheck2') return '체크박스2'
    if (b === 'consentCheck3') return '체크박스3'
    if (b === 'consentCheck4') return '체크박스4'
    if (b === 'consentCheck5') return '체크박스5'
    const k = String(fallbackKey || '').trim()
    return k || b
  }, [])

  const consentDynamicMeta = useMemo(() => {
    // 선택 보험사 템플릿의 필드 중 "사용자 입력이 필요한 것"을 bindKey 기준으로 합집합/중복 제거한다.
    const meta = new Map() // bindKey -> { bindKey, label, insurers:Set<string>, isCommon:boolean }

    const ids = Array.isArray(selectedInsurerOrgIds) ? selectedInsurerOrgIds : []
    for (const id of ids) {
      const tpl = templatesByInsurer[String(id)]
      const fields = Array.isArray(tpl?.fields) ? tpl.fields : []
      for (const f of fields) {
        const binding = (f?.bindKey && String(f.bindKey).trim()) ? String(f.bindKey).trim() : String(f?.key || '').trim()
        if (!binding) continue

        // 자동값은 입력 불필요 (템플릿에서 좌표는 있어도 화면 입력은 안 받음)
        const vt = String(f?.valueType || '').toUpperCase()
        if (CONSENT_AUTO_BINDINGS.has(binding)) continue
        // TODAY 계열(출력일/출력년/출력월/출력일-일/출력년-2자리)도 자동값으로 취급
        if (vt === 'TODAY' || vt === 'TODAY_YYYY' || vt === 'TODAY_YY' || vt === 'TODAY_MM' || vt === 'TODAY_DD') continue
        if (vt === 'CHECK_V') continue

        const isCommon = CONSENT_COMMON_BINDINGS.has(binding)
        const label = consentBindingLabel(binding, f?.key)

        if (!meta.has(binding)) {
          meta.set(binding, { bindKey: binding, label, insurers: new Set([String(id)]), isCommon })
        } else {
          meta.get(binding).insurers.add(String(id))
          // label은 먼저 들어온 값을 유지(보험사마다 key가 다를 수 있어 혼선을 피함)
        }
      }
    }

    // customerName은 템플릿에 없어도 항상 받는다(정책상 필수)
    if (!meta.has('customerName')) {
      meta.set('customerName', { bindKey: 'customerName', label: '성명', insurers: new Set(ids.map(String)), isCommon: true })
    }

    const list = Array.from(meta.values())
    // common 먼저, 그 다음 라벨 기준 정렬
    list.sort((a, b) => {
      if (a.isCommon !== b.isCommon) return a.isCommon ? -1 : 1
      return String(a.label || '').localeCompare(String(b.label || ''))
    })
    return list
  }, [selectedInsurerOrgIds, templatesByInsurer, CONSENT_COMMON_BINDINGS, CONSENT_AUTO_BINDINGS, consentBindingLabel])

  const consentRequiredBindings = useMemo(() => {
    // 요구사항: 기본 필수는 성명만.
    // 다만 선택 보험사 템플릿에 해당 필드가 존재하면(입력필드라면) 그 순간 필수로 간주한다.
    const req = new Set(['customerName'])
    for (const m of consentDynamicMeta) {
      if (m.bindKey === 'customerName') continue
      // 선택 보험사 중 1개라도 사용하면 입력 필요(필수)
      if (m.insurers && m.insurers.size > 0) req.add(m.bindKey)
    }
    return req
  }, [consentDynamicMeta])

  const buildExtraFieldsPayload = React.useCallback(() => {
    const out = {}
    for (const [k, v] of Object.entries(extraInputs || {})) {
      const binding = String(k || '').trim()
      if (!binding) continue
      if (CONSENT_COMMON_BINDINGS.has(binding)) continue
      if (CONSENT_AUTO_BINDINGS.has(binding)) continue
      if (v == null) continue
      out[binding] = String(v)
    }
    return out
  }, [extraInputs, CONSENT_COMMON_BINDINGS, CONSENT_AUTO_BINDINGS])

  const tabButtonClass = (active) =>
    `rounded px-3 py-2 text-sm border ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50 text-slate-700'}`

  const onChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))
  const onSetupChange = (key) => (e) => setSetup((prev) => ({ ...prev, [key]: e.target.value }))

  const uploadFile = async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    // NOTE: FormData는 Content-Type(boundary 포함)를 브라우저가 자동으로 설정하므로 수동 지정하지 않는다.
    const res = await api.post('/files', formData)

    // CloudFront/S3 SPA 라우팅(403/404 -> index.html 200) 오구성 시
    // API 응답이 JSON이 아닌 HTML로 내려올 수 있다. 이 경우 fileId 파싱이 실패하므로 명확한 에러로 바꾼다.
    const ct = String(res?.headers?.['content-type'] || '').toLowerCase()
    if (ct.includes('text/html') || ct.includes('application/xhtml')) {
      throw new Error('api_misconfigured_html_response(/v1/files)')
    }

    const payload = res?.data?.data ?? res?.data ?? {}
    if (payload && payload.success === false) {
      throw new Error(payload?.message || 'upload_failed')
    }
    const fileId = payload.fileId || payload.id || payload.filename
    if (!fileId) throw new Error('no_file_id')
    return fileId
  }

  const openFilePreview = async (fileId) => {
    if (!fileId) return
    try {
      const res = await api.get(`/files/${fileId}/preview`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      // revoke는 즉시 하면 새 창이 못 읽는 케이스가 있어 딜레이 처리
      setTimeout(() => {
        try { window.URL.revokeObjectURL(url) } catch { }
      }, 30_000)
    } catch (e) {
      console.error(e)
      show('파일 미리보기에 실패했습니다. (권한/파일ID/서버 상태 확인)', 'error')
    }
  }

  const updateField = (key, patch) => {
    // key가 동일한 필드가 여러 페이지에 있을 수 있으므로,
    // pageIndex가 전달되면 해당 pageIndex의 1개만 업데이트한다.
    const targetPageIndex = patch && patch.pageIndex != null ? Number(patch.pageIndex) : null
    setSetup((prev) => {
      const list = Array.isArray(prev.fields) ? prev.fields : []
      let updated = false
      const next = list.map((f) => {
        if (updated) return f
        if (f.key !== key) return f
        if (targetPageIndex != null && Number(f.pageIndex ?? 0) !== targetPageIndex) return f
        updated = true
        return { ...f, ...patch }
      })
      // pageIndex 매칭이 실패했으면 key 기준으로 첫 1개만 fallback 업데이트
      if (!updated) {
        let fallbackDone = false
        return {
          ...prev,
          fields: next.map((f) => {
            if (fallbackDone) return f
            if (f.key !== key) return f
            fallbackDone = true
            return { ...f, ...patch }
          }),
        }
      }
      return { ...prev, fields: next }
    })
  }

  const ensureField = (key) => {
    setSetup((prev) => {
      const exists = (prev.fields || []).some((f) => f.key === key)
      if (exists) return prev
      const known = new Set([
        'customerName',
        'birthDate',
        'phoneNumber',
        'printedAt',
        'printedAtYear',
        'printedAtMonth',
        'printedAtDay',
        'actorName',
        'consentCheck',
        'consentCheck1',
        'consentCheck2',
        'consentCheck3',
        'consentCheck4',
        'consentCheck5',
      ])
      const bindKey = known.has(String(key || '')) ? String(key) : ''
      return {
        ...prev,
        fields: [
          ...(prev.fields || []),
          { key, bindKey, pageIndex: Math.max(0, Number(setupCurrentPageIndex ?? 0)), x: 0.1, y: 0.1, w: 0.2, h: 0.03, fontSize: 16, align: 'left', valueType: '', value: '' },
        ],
      }
    })
  }

  const makeUniqueFieldKey = React.useCallback((base) => {
    const used = new Set((setup.fields || []).map(f => String(f?.key || '').trim()).filter(Boolean))
    const safeBase = String(base || 'field').trim() || 'field'
    if (!used.has(safeBase)) return safeBase
    for (let i = 1; i < 9999; i += 1) {
      const k = `${safeBase}_${i}`
      if (!used.has(k)) return k
    }
    return `${safeBase}_${Date.now()}`
  }, [setup.fields])

  const fieldsJson = useMemo(() => {
    // array format: allow multiple fields across pages even if they bind to same value
    const arr = []
    for (const f of (setup.fields || [])) {
      if (!f?.key) continue
      const fs = Number(f.fontSize)
      arr.push({
        key: String(f.key),
        bindKey: String(f.bindKey || ''),
        pageIndex: Number(f.pageIndex ?? 0),
        x: Number(f.x ?? 0),
        y: Number(f.y ?? 0),
        w: Number(f.w ?? 0),
        h: Number(f.h ?? 0),
        fontSize: (Number.isFinite(fs) && fs > 0) ? fs : 16,
        align: (f.align || 'left'),
        valueType: (f.valueType || ''),
        value: (f.value || ''),
      })
    }
    return JSON.stringify(arr, null, 2)
  }, [setup.fields])

  // NOTE: 이전 구현(페이지 유지 문제로 파일 스코프 PdfTemplateMapper로 대체). 삭제 예정.
  // eslint-disable-next-line no-unused-vars
  function PdfTemplateMapper_OLD({ fileId, fields, selectedKey, onSelectKey, onSetBox }) {
    const [busy, setBusy] = useState(false)
    const [err, setErr] = useState('')
    // 기본 배율 100%
    const [pageInfo, setPageInfo] = useState({ width: 0, height: 0, scale: 1.0 })
    const [numPages, setNumPages] = useState(0)
    const [pageNum, setPageNum] = useState(1) // 1-based
    const [pdfBytes, setPdfBytes] = useState(null) // ArrayBuffer
    const [pdfDoc, setPdfDoc] = useState(null) // PDFDocumentProxy
    const [drag, setDrag] = useState(null) // { startX, startY, curX, curY }
    const [moving, setMoving] = useState(null) // { key, startX, startY, originX, originY, wRatio, hRatio, pageIndex, boxWpx, boxHpx }

    const canvasId = useMemo(() => `pdf-canvas-${String(fileId || '').replace(/[^a-zA-Z0-9_-]/g, '')}`, [fileId])

    useEffect(() => {
      let cancelled = false
      async function loadPdf() {
        if (!fileId) return
        setBusy(true)
        setErr('')
        try {
          const res = await api.get(`/files/${fileId}/preview`, { responseType: 'arraybuffer' })
          if (cancelled) return
          setPdfBytes(res.data)
          setPageNum(1)
        } catch (e) {
          console.error(e)
          if (!cancelled) setErr('PDF를 렌더링하지 못했습니다. (파일ID/권한/형식을 확인해주세요)')
        } finally {
          if (!cancelled) setBusy(false)
        }
      }
      setPdfBytes(null)
      setPdfDoc(null)
      setNumPages(0)
      setPageNum(1)
      loadPdf()
      return () => { cancelled = true }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId, canvasId])

    // PDF 문서는 한 번만 로드해서 캐시한다 (페이지 이동 시 재사용)
    useEffect(() => {
      let cancelled = false
      async function loadDoc() {
        if (!pdfBytes) {
          setPdfDoc(null)
          setNumPages(0)
          return
        }
        setBusy(true)
        setErr('')
        try {
          const { loadingTask, pdf } = await getDocumentWithWorkerFallback({ data: pdfBytes })
          if (cancelled) {
            try { loadingTask.destroy?.() } catch { }
            try { pdf.destroy?.() } catch { }
            return
          }
          setPdfDoc(pdf)
          setNumPages(pdf.numPages || 0)
        } catch (e) {
          console.error(e)
          if (!cancelled) {
            const msg = e?.message ? ` (${e.message})` : ''
            setErr(`PDF를 렌더링하지 못했습니다.${msg}`)
          }
          setPdfDoc(null)
          setNumPages(0)
        } finally {
          if (!cancelled) setBusy(false)
        }
      }
      loadDoc()
      return () => {
        cancelled = true
        // pdfDoc cleanup은 별도 effect에서 처리(상태 변경 타이밍 이슈 방지)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfBytes])

    // pdfDoc가 교체될 때 이전 문서 정리
    useEffect(() => {
      return () => {
        try { pdfDoc?.destroy?.() } catch { }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfDoc])

    // 선택된 필드가 특정 페이지에 있으면 자동으로 그 페이지로 이동
    useEffect(() => {
      if (!selectedKey) return
      const f = (fields || []).find(x => x?.key === selectedKey)
      if (!f) return
      const pi = Number(f.pageIndex ?? 0)
      const next = Math.max(1, pi + 1)
      if (next !== pageNum) setPageNum(next)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey])

    useEffect(() => {
      let cancelled = false
      async function renderPage() {
        if (!fileId || !pdfDoc) return
        setBusy(true)
        setErr('')
        try {
          const n = pdfDoc.numPages || 0
          const safePageNum = Math.max(1, Math.min(n || 1, pageNum || 1))
          if (safePageNum !== pageNum) setPageNum(safePageNum)

          const page = await pdfDoc.getPage(safePageNum)
          const scale = pageInfo.scale || 1.0
          const viewport = page.getViewport({ scale })
          if (cancelled) return

          const canvas = document.getElementById(canvasId)
          if (!canvas) return
          const ctx = canvas.getContext('2d')
          canvas.width = Math.floor(viewport.width)
          canvas.height = Math.floor(viewport.height)
          setPageInfo((prev) => ({ ...prev, width: canvas.width, height: canvas.height, scale }))

          const task = page.render({ canvasContext: ctx, viewport })
          try {
            await task.promise
          } catch (e) {
            // page change during render -> cancel-like error may happen; surface only if not cancelled
            if (!cancelled) throw e
          }
        } catch (e) {
          console.error(e)
          if (!cancelled) {
            const msg = e?.message ? ` (${e.message})` : ''
            setErr(`PDF를 렌더링하지 못했습니다.${msg}`)
          }
        } finally {
          if (!cancelled) setBusy(false)
        }
      }
      renderPage()
      return () => { cancelled = true }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileId, pdfDoc, canvasId, pageNum, pageInfo.scale])

    const containerStyle = {
      width: pageInfo.width ? `${pageInfo.width}px` : '100%',
      height: pageInfo.height ? `${pageInfo.height}px` : '320px',
    }

    const getPos = (e) => {
      const el = e.currentTarget
      const rect = el.getBoundingClientRect()
      const clientX = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX
      const clientY = (e.touches && e.touches[0]) ? e.touches[0].clientY : e.clientY
      const x = Math.max(0, Math.min(rect.width, clientX - rect.left))
      const y = Math.max(0, Math.min(rect.height, clientY - rect.top))
      return { x, y, w: rect.width, h: rect.height }
    }

    const onPointerDown = (e) => {
      if (moving) return
      if (!selectedKey) return
      e.preventDefault()
      const p = getPos(e)
      setDrag({ startX: p.x, startY: p.y, curX: p.x, curY: p.y, w: p.w, h: p.h })
    }
    const onPointerMove = (e) => {
      if (moving) return
      if (!drag) return
      e.preventDefault()
      const p = getPos(e)
      setDrag((prev) => prev ? ({ ...prev, curX: p.x, curY: p.y }) : prev)
    }
    const onPointerUp = (e) => {
      if (moving) return
      if (!drag) return
      e.preventDefault()
      const { startX, startY, curX, curY, w, h } = drag
      const pageIndex = Math.max(0, (pageNum || 1) - 1)

      // 클릭(거의 드래그 없음) 시: 기존 박스가 있으면 "이동"으로 처리(크기 유지)
      const dx = Math.abs(curX - startX)
      const dy = Math.abs(curY - startY)
      const clickish = (dx < 4 && dy < 4)
      if (clickish) {
        const existing =
          (fields || []).find(ff => ff?.key === selectedKey && Number(ff?.pageIndex ?? 0) === pageIndex)
          || (fields || []).find(ff => ff?.key === selectedKey)
        const keepW = Number(existing?.w ?? 0.2)
        const keepH = Number(existing?.h ?? 0.03)
        const rx = startX / w
        const ry = startY / h
        onSetBox && onSetBox(selectedKey, { pageIndex, x: rx, y: ry, w: keepW, h: keepH })
        setDrag(null)
        return
      }

      // 드래그 박스 생성/리사이즈
      const x1 = Math.min(startX, curX)
      const y1 = Math.min(startY, curY)
      const x2 = Math.max(startX, curX)
      const y2 = Math.max(startY, curY)
      const bw = Math.max(1, x2 - x1)
      const bh = Math.max(1, y2 - y1)
      const rx = x1 / w
      const ry = y1 / h
      const rw = bw / w
      const rh = bh / h
      onSetBox && onSetBox(selectedKey, { pageIndex, x: rx, y: ry, w: rw, h: rh })
      setDrag(null)
    }

    // 박스 이동(드래그) 처리
    useEffect(() => {
      if (!moving) return
      function onMove(ev) {
        try {
          const isTouch = ev.touches && ev.touches[0]
          const clientX = isTouch ? ev.touches[0].clientX : ev.clientX
          const clientY = isTouch ? ev.touches[0].clientY : ev.clientY
          const dx = clientX - moving.startX
          const dy = clientY - moving.startY
          const pageW = moving.pageW || pageInfo.width || 1
          const pageH = moving.pageH || pageInfo.height || 1
          const maxX = Math.max(0, pageW - (moving.boxWpx || 0))
          const maxY = Math.max(0, pageH - (moving.boxHpx || 0))
          const nxPx = Math.max(0, Math.min(maxX, (moving.originX || 0) + dx))
          const nyPx = Math.max(0, Math.min(maxY, (moving.originY || 0) + dy))
          const nx = nxPx / pageW
          const ny = nyPx / pageH
          // 이동 중에는 parent state를 매번 바꾸지 않고(렌더 튐 방지) 내부 preview만 업데이트한다.
          setMoving((prev) => prev ? ({ ...prev, curXRatio: nx, curYRatio: ny }) : prev)
        } catch (e) {
          // ignore
        }
      }
      function onUp() {
        try {
          const x = moving.curXRatio != null ? moving.curXRatio : Number(moving.originX || 0) / (moving.pageW || pageInfo.width || 1)
          const y = moving.curYRatio != null ? moving.curYRatio : Number(moving.originY || 0) / (moving.pageH || pageInfo.height || 1)
          onSetBox && onSetBox(moving.key, {
            pageIndex: moving.pageIndex,
            x,
            y,
            w: moving.wRatio,
            h: moving.hRatio,
          })
        } catch { }
        setMoving(null)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
      window.addEventListener('touchmove', onMove, { passive: false })
      window.addEventListener('touchend', onUp)
      return () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        window.removeEventListener('touchmove', onMove)
        window.removeEventListener('touchend', onUp)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moving, pageInfo.width, pageInfo.height, onSetBox])

    const boxStyle = (f) => {
      const fPageIndex = Number(f?.pageIndex ?? 0)
      const overrideX = (moving && moving.key === f?.key && moving.pageIndex === fPageIndex && moving.curXRatio != null)
        ? moving.curXRatio
        : Number(f?.x ?? 0)
      const overrideY = (moving && moving.key === f?.key && moving.pageIndex === fPageIndex && moving.curYRatio != null)
        ? moving.curYRatio
        : Number(f?.y ?? 0)
      const px = overrideX * pageInfo.width
      const py = overrideY * pageInfo.height
      const pw = Number(f?.w ?? 0) * pageInfo.width
      const ph = Number(f?.h ?? 0) * pageInfo.height
      return {
        left: `${px}px`,
        top: `${py}px`,
        width: `${pw}px`,
        height: `${ph}px`,
      }
    }

    const dragRect = (() => {
      if (!drag) return null
      const x1 = Math.min(drag.startX, drag.curX)
      const y1 = Math.min(drag.startY, drag.curY)
      const x2 = Math.max(drag.startX, drag.curX)
      const y2 = Math.max(drag.startY, drag.curY)
      return { left: x1, top: y1, width: Math.max(1, x2 - x1), height: Math.max(1, y2 - y1) }
    })()

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">PDF 미리보기 — 드래그로 좌표 지정</div>
          <div className="text-xs text-slate-500">{busy ? '렌더링 중…' : (pageInfo.width ? `${pageInfo.width}×${pageInfo.height}px` : '')}</div>
        </div>
        {err && <div className="text-xs text-red-500">{err}</div>}

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border px-2 py-1 text-sm disabled:opacity-50"
              disabled={!numPages || pageNum <= 1 || busy}
              onClick={() => setPageNum((p) => Math.max(1, (p || 1) - 1))}
            >
              이전
            </button>
            <div className="text-sm">
              페이지{' '}
              <input
                className="w-14 rounded border px-2 py-1 text-sm"
                value={pageNum}
                onChange={(e) => setPageNum(Number(e.target.value || 1))}
                onBlur={() => setPageNum((p) => Math.max(1, Math.min(numPages || 1, Number(p || 1))))}
                disabled={busy || !numPages}
              />{' '}
              / {numPages || '—'}
            </div>
            <button
              type="button"
              className="rounded border px-2 py-1 text-sm disabled:opacity-50"
              disabled={!numPages || pageNum >= numPages || busy}
              onClick={() => setPageNum((p) => Math.min(numPages || 1, (p || 1) + 1))}
            >
              다음
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">확대</span>
            <select
              className="rounded border px-2 py-1 text-sm"
              value={pageInfo.scale}
              onChange={(e) => setPageInfo((prev) => ({ ...prev, scale: Number(e.target.value) }))}
              disabled={busy}
            >
              <option value={1.0}>100%</option>
              <option value={1.25}>125%</option>
              <option value={1.5}>150%</option>
              <option value={2.0}>200%</option>
            </select>
          </div>

          <div className="text-xs text-slate-500">
            현재 페이지 index: {Math.max(0, (pageNum || 1) - 1)}
          </div>
        </div>

        <div className="rounded border bg-white overflow-auto">
          <div className="relative select-none" style={containerStyle}>
            <canvas id={canvasId} className="absolute left-0 top-0" />
            {/* overlay */}
            <div
              className="absolute left-0 top-0"
              style={{ width: `${pageInfo.width}px`, height: `${pageInfo.height}px` }}
              onMouseDown={onPointerDown}
              onMouseMove={onPointerMove}
              onMouseUp={onPointerUp}
              onMouseLeave={onPointerUp}
              onTouchStart={onPointerDown}
              onTouchMove={onPointerMove}
              onTouchEnd={onPointerUp}
            >
              {(fields || [])
                .filter(f =>
                  f?.key
                  && (Number(f?.pageIndex ?? 0) === Math.max(0, (pageNum || 1) - 1))
                  && (Number(f?.w || 0) > 0)
                  && (Number(f?.h || 0) > 0)
                )
                .map((f) => {
                const active = f.key === selectedKey
                return (
                  <button
                    key={f.key}
                    type="button"
                    className={`absolute border cursor-move ${active ? 'border-brand-sky bg-brand-sky/10' : 'border-amber-400 bg-amber-400/10'}`}
                    style={boxStyle(f)}
                    onClick={(e) => {
                      e.stopPropagation()
                      // 박스 클릭 시 해당 필드 선택 + 해당 페이지로 이동(안전)
                      const next = Math.max(1, Number(f.pageIndex ?? 0) + 1)
                      if (next !== pageNum) setPageNum(next)
                      onSelectKey && onSelectKey(f.key)
                    }}
                    onMouseDown={(e) => {
                      // 박스 이동 시작 (새 박스 그리기 방지)
                      e.stopPropagation()
                      if (e.button !== 0) return
                      const rect = e.currentTarget.parentElement?.getBoundingClientRect()
                      const clientX = e.clientX
                      const clientY = e.clientY
                      const pageW = pageInfo.width || rect?.width || 1
                      const pageH = pageInfo.height || rect?.height || 1
                      const originX = Number(f?.x ?? 0) * pageW
                      const originY = Number(f?.y ?? 0) * pageH
                      const boxWpx = Number(f?.w ?? 0) * pageW
                      const boxHpx = Number(f?.h ?? 0) * pageH
                      setMoving({
                        key: f.key,
                        startX: clientX,
                        startY: clientY,
                        originX,
                        originY,
                        wRatio: Number(f?.w ?? 0),
                        hRatio: Number(f?.h ?? 0),
                        pageIndex: Number(f?.pageIndex ?? 0),
                        boxWpx,
                        boxHpx,
                        pageW,
                        pageH,
                      })
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation()
                      const t = e.touches?.[0]
                      if (!t) return
                      const rect = e.currentTarget.parentElement?.getBoundingClientRect()
                      const pageW = pageInfo.width || rect?.width || 1
                      const pageH = pageInfo.height || rect?.height || 1
                      const originX = Number(f?.x ?? 0) * pageW
                      const originY = Number(f?.y ?? 0) * pageH
                      const boxWpx = Number(f?.w ?? 0) * pageW
                      const boxHpx = Number(f?.h ?? 0) * pageH
                      setMoving({
                        key: f.key,
                        startX: t.clientX,
                        startY: t.clientY,
                        originX,
                        originY,
                        wRatio: Number(f?.w ?? 0),
                        hRatio: Number(f?.h ?? 0),
                        pageIndex: Number(f?.pageIndex ?? 0),
                        boxWpx,
                        boxHpx,
                        pageW,
                        pageH,
                      })
                    }}
                    title={f.key}
                  >
                    <span className={`absolute -top-5 left-0 text-[11px] px-1 rounded ${active ? 'bg-brand-sky text-white' : 'bg-amber-400 text-slate-900'}`}>
                      {f.key}
                    </span>
                  </button>
                )
              })}
              {dragRect && (
                <div
                  className="absolute border-2 border-red-500 bg-red-500/10 pointer-events-none"
                  style={{ left: `${dragRect.left}px`, top: `${dragRect.top}px`, width: `${dragRect.width}px`, height: `${dragRect.height}px` }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const saveTemplate = async () => {
    if (!setup.insurerOrgId) {
      show('보험사를 먼저 선택해 주세요.', 'error')
      return
    }
    if (!setup.templateFileId) {
      show('템플릿 PDF를 업로드(또는 fileId 입력)해 주세요.', 'error')
      return
    }
    try {
      setSavingTemplate(true)
      const fieldsObj = JSON.parse(fieldsJson)
      await api.post('/consent-forms/templates', {
        insurerOrgId: Number(setup.insurerOrgId),
        templateFileId: setup.templateFileId,
        fields: fieldsObj,
      })
      show('템플릿(좌표)이 저장되었습니다.', 'success')
      // 저장 직후, 해당 보험사의 활성 템플릿 캐시를 갱신해 좌표가 즉시 "보험사별"로 로드되게 한다.
      const insurerId = String(setup.insurerOrgId)
      setTemplatesByInsurer((prev) => ({
        ...(prev || {}),
        [insurerId]: {
          fields: Array.isArray(setup.fields) ? setup.fields : [],
          raw: {
            insurerOrgId: Number(setup.insurerOrgId),
            templateFileId: String(setup.templateFileId || ''),
            fieldsJson: String(fieldsJson || ''),
            active: true,
          },
        },
      }))
      lastSetupAutoLoadedInsurerIdRef.current = null
    } catch (e) {
      console.error(e)
      show('템플릿 저장에 실패했습니다.', 'error')
    } finally {
      setSavingTemplate(false)
    }
  }

  const saveIssued = async () => {
    if (!setup.insurerOrgId) {
      show('보험사를 먼저 선택해 주세요.', 'error')
      return
    }
    if (!setup.issuedFileId) {
      show('발행본 PDF를 업로드(또는 fileId 입력)해 주세요.', 'error')
      return
    }
    try {
      setSavingIssued(true)
      await api.post('/consent-forms/issued-pdfs', {
        insurerOrgId: Number(setup.insurerOrgId),
        issuedFileId: setup.issuedFileId,
      })
      show('발행본 PDF가 설정되었습니다.', 'success')
    } catch (e) {
      console.error(e)
      show('발행본 설정에 실패했습니다.', 'error')
    } finally {
      setSavingIssued(false)
    }
  }

  const handleDownloadConsent = async (e) => {
    e.preventDefault()
    if (!form.customerName.trim()) {
      show('고객명을 입력해 주세요.', 'error')
      return
    }
    if (!Array.isArray(selectedInsurerOrgIds) || selectedInsurerOrgIds.length === 0) {
      show('보험사를 1개 이상 선택해 주세요.', 'error')
      return
    }

    // 선택 보험사 템플릿 기준으로 필수 입력 검증
    const missing = []
    for (const m of consentDynamicMeta) {
      const binding = m?.bindKey
      if (!binding) continue
      if (!consentRequiredBindings.has(binding)) continue
      if (binding === 'customerName') continue
      if (binding === 'birthDate') {
        if (!form.birthDate) missing.push(m)
        continue
      }
      if (binding === 'phoneNumber') {
        if (!form.phoneNumber.trim()) missing.push(m)
        continue
      }
      const v = (extraInputs || {})[binding]
      if (!String(v || '').trim()) missing.push(m)
    }
    if (missing.length > 0) {
      const m = missing[0]
      const insurerNames = Array.from(m.insurers || []).map(id => insurerNameById.get(String(id)) || String(id))
      show(`필수 입력값이 비어있습니다: ${m.label} (사용 보험사: ${insurerNames.join(', ')})`, 'error')
      return
    }

    try {
      setDownloading(true)

      const customerName = form.customerName.trim()
      const birthDate = form.birthDate
      const phoneNumber = form.phoneNumber.trim()
      const extraFieldsShared = buildExtraFieldsPayload()

      let successCount = 0
      const failed = []
      for (const id of selectedInsurerOrgIds) {
        // 발행본 없는 보험사는 선택 불가지만, 방어로직 추가
        if (!issuedAvailableOrgIds.has(String(id))) {
          failed.push(`${insurerNameById.get(String(id)) || id}: 발행본 미업로드`)
          continue
        }
        const extraFields = extraFieldsShared
        const payload = {
          customerName,
          birthDate,
          phoneNumber,
          insurerOrgId: Number(id),
          extraFields,
        }
        try {
          const res = await api.post('/consent-forms/render', payload, { responseType: 'blob' })
          const blob = new Blob([res.data], { type: 'application/pdf' })
          const cd = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'] || ''
          const headerFilename = filenameFromContentDisposition(cd)
          const today = ymdCompact(new Date())
          const insurerName = insurerNameById.get(String(id)) || String(id)
          const fallbackFilename = `${customerName}_${insurerName}_동의서_${today}.pdf`
          const filename = headerFilename || fallbackFilename

          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = filename
          document.body.appendChild(a)
          a.click()
          a.remove()
          window.URL.revokeObjectURL(url)
          successCount += 1
        } catch (err) {
          console.error(err)
          failed.push(`${insurerNameById.get(String(id)) || id}`)
        }
      }

      if (successCount > 0) {
        show(`동의서 다운로드가 시작되었습니다. (${successCount}건)`, 'success')
      }
      if (failed.length > 0) {
        show(`일부 보험사 출력 실패: ${failed.join(', ')}`, 'error')
      }
    } catch (err) {
      console.error(err)
      if (err?.response?.status === 404) {
        show('서버에 동의서 출력 API가 아직 준비되지 않았습니다. (consent-forms/render)', 'error')
      } else {
        show('동의서 다운로드에 실패했습니다.', 'error')
      }
    } finally {
      setDownloading(false)
    }
  }

  const downloadPdfBytes = (bytes, filename) => {
    if (!bytes) return
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || `동의서_${ymdCompact(new Date())}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const buildConsentFilename = (insurerOrgId) => {
    const customerName = form.customerName.trim() || '고객'
    const insurerName = insurerNameById.get(String(insurerOrgId)) || String(insurerOrgId)
    const today = ymdCompact(new Date())
    return `${customerName}_${insurerName}_동의서_${today}.pdf`
  }

  const renderPreviewPdf = async (insurerOrgId) => {
    const id = String(insurerOrgId || '')
    if (!id) throw new Error('no_insurer')
    const payload = {
      customerName: form.customerName.trim(),
      birthDate: form.birthDate,
      phoneNumber: form.phoneNumber.trim(),
      insurerOrgId: Number(id),
      extraFields: buildExtraFieldsPayload(),
    }
    const res = await api.post('/consent-forms/render', payload, { responseType: 'arraybuffer' })
    // pdf.js가 buffer를 detach할 수 있으므로 복사본 저장
    const bytes = res.data ? res.data.slice(0) : res.data
    setPreviewPdfBytesByOrgId((prev) => ({ ...prev, [id]: bytes }))
    return bytes
  }

  const handlePreviewConsent = async () => {
    if (!form.customerName.trim()) {
      show('고객명을 입력해 주세요.', 'error')
      return
    }
    if (!Array.isArray(selectedInsurerOrgIds) || selectedInsurerOrgIds.length === 0) {
      show('미리보기할 보험사를 1개 이상 선택해 주세요.', 'error')
      return
    }

    // 선택 보험사 템플릿 기준으로 필수 입력 검증
    const missing = []
    for (const m of consentDynamicMeta) {
      const binding = m?.bindKey
      if (!binding) continue
      if (!consentRequiredBindings.has(binding)) continue
      if (binding === 'customerName') continue
      if (binding === 'birthDate') {
        if (!form.birthDate) missing.push(m)
        continue
      }
      if (binding === 'phoneNumber') {
        if (!form.phoneNumber.trim()) missing.push(m)
        continue
      }
      const v = (extraInputs || {})[binding]
      if (!String(v || '').trim()) missing.push(m)
    }
    if (missing.length > 0) {
      const m = missing[0]
      const insurerNames = Array.from(m.insurers || []).map(id => insurerNameById.get(String(id)) || String(id))
      show(`필수 입력값이 비어있습니다: ${m.label} (사용 보험사: ${insurerNames.join(', ')})`, 'error')
      return
    }
    const initial = previewInsurerOrgId || selectedInsurerOrgIds[0]
    setPreviewInsurerOrgId(String(initial))
    setPreviewModalErr('')
    setPreviewModalOpen(true)
  }

  // 프리뷰 모달이 열렸거나 보험사 선택이 바뀌면, 해당 보험사의 프리뷰를 생성/캐시한다.
  useEffect(() => {
    let ignore = false
    async function ensurePreview() {
      if (!previewModalOpen) return
      const id = String(previewInsurerOrgId || (Array.isArray(selectedInsurerOrgIds) ? selectedInsurerOrgIds[0] : ''))
      if (!id) return
      setPreviewModalErr('')
      setPreviewFilenameHint(buildConsentFilename(id))
      if (!issuedAvailableOrgIds.has(String(id))) {
        setPreviewModalErr('내 발행본이 없어 프리뷰를 만들 수 없습니다. (동의서 업로드 필요)')
        return
      }
      if (previewPdfBytesByOrgId[id]) return
      try {
        setPreviewing(true)
        await renderPreviewPdf(id)
      } catch (err) {
        console.error(err)
        if (ignore) return
        if (err?.response?.status === 409) {
          setPreviewModalErr('템플릿/발행본 레이아웃 불일치로 프리뷰를 생성할 수 없습니다. (운영자 템플릿 확인 필요)')
        } else if (err?.response?.status === 404) {
          setPreviewModalErr('템플릿 또는 발행본이 없습니다. (템플릿/동의서 업로드 확인)')
        } else {
          setPreviewModalErr('프리뷰 생성에 실패했습니다.')
        }
      } finally {
        if (!ignore) setPreviewing(false)
      }
    }
    ensurePreview()
    return () => { ignore = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewModalOpen, previewInsurerOrgId, selectedInsurerOrgIds, issuedAvailableOrgIds, previewPdfBytesByOrgId])

  const handleUploadIssuedFromUploadPage = async (file) => {
    if (!uploadInsurerOrgId) {
      show('보험사를 선택해 주세요.', 'error')
      return
    }
    const tpl = templatesByInsurer[String(uploadInsurerOrgId)]?.raw
    const templateFileId = tpl?.templateFileId
    if (!templateFileId) {
      show('해당 보험사에 활성 템플릿이 없습니다. (템플릿/좌표 설정 후 업로드 가능)', 'error')
      return
    }

    // 업로드 즉시 레이아웃 검증(클라이언트): NG면 서버 업로드 자체를 막는다.
    try {
      setUploadLayoutCheck({ status: 'checking', message: '레이아웃 검증 중…', details: null })
      const ab = await file.arrayBuffer()
      const { pdf } = await getDocumentWithWorkerFallback({ data: ab })
      const pageCount = pdf.numPages || 0
      const expectedCount = Number(tpl?.expectedPageCount ?? 0)
      let expectedSizes = []
      try {
        expectedSizes = tpl?.expectedPageSizesJson ? JSON.parse(tpl.expectedPageSizesJson) : []
      } catch {
        expectedSizes = []
      }
      const sizes = []
      for (let i = 1; i <= pageCount; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const page = await pdf.getPage(i)
        const vp = page.getViewport({ scale: 1.0 })
        sizes.push({ w: vp.width, h: vp.height })
      }
      try { pdf.destroy?.() } catch { }

      if (expectedCount && pageCount !== expectedCount) {
        setUploadLayoutCheck({ status: 'ng', message: `페이지 수 불일치 (템플릿 ${expectedCount}p / 발행본 ${pageCount}p)`, details: { expectedCount, pageCount } })
        show('발행본 레이아웃이 템플릿과 달라 업로드할 수 없습니다. (페이지 수 불일치)', 'error')
        return
      }
      if (Array.isArray(expectedSizes) && expectedSizes.length > 0) {
        const tol = 0.5
        const n = Math.min(expectedSizes.length, sizes.length)
        let mismatch = null
        for (let i = 0; i < n; i += 1) {
          const a = expectedSizes[i]
          const b = sizes[i]
          if (!a || !b) continue
          if (Math.abs(Number(a.w) - Number(b.w)) > tol || Math.abs(Number(a.h) - Number(b.h)) > tol) {
            mismatch = { page: i + 1, expected: a, actual: b }
            break
          }
        }
        if (mismatch || expectedSizes.length !== sizes.length) {
          const msg = mismatch
            ? `페이지 크기 불일치 (p${mismatch.page})`
            : '페이지 크기 불일치'
          setUploadLayoutCheck({ status: 'ng', message: msg, details: mismatch || { expectedSizes, sizes } })
          show('발행본 레이아웃이 템플릿과 달라 업로드할 수 없습니다. (페이지 크기 불일치)', 'error')
          return
        }
      }

      setUploadLayoutCheck({ status: 'ok', message: '레이아웃 검증 OK', details: { pageCount } })
    } catch (e) {
      console.error(e)
      setUploadLayoutCheck({ status: 'idle', message: '', details: null })
      // 검증 실패는 업로드를 막는다(안전)
      show('발행본 PDF 레이아웃 검증에 실패했습니다. (PDF 손상/권한/브라우저)', 'error')
      return
    }
    try {
      setUploadingIssuedFromUploadPage(true)
      const fileId = await uploadFile(file)
      const formVersion = tpl?.formVersion || undefined
      await api.post('/consent-forms/issued-pdfs', {
        insurerOrgId: Number(uploadInsurerOrgId),
        formVersion,
        issuedFileId: fileId,
      })
      setIssuedAvailableOrgIds((prev) => {
        const next = new Set(Array.from(prev || []))
        next.add(String(uploadInsurerOrgId))
        return next
      })
      show('발행본 PDF 등록이 완료되었습니다.', 'success')
    } catch (e) {
      console.error(e)
      if (e?.response?.status === 409) {
        const code = e?.response?.data?.message || e?.response?.data?.error || e?.response?.data?.data || e?.response?.data
        show(`발행본 등록 실패(레이아웃/템플릿 확인 필요): ${String(code || '409')}`, 'error')
      } else {
        show('발행본 PDF 등록에 실패했습니다.', 'error')
      }
    } finally {
      setUploadingIssuedFromUploadPage(false)
    }
  }

  return (
    <section className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-bold">고객관리</h1>
          <MenuGuideButton slug="support-customers" title="고객관리 가이드" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        <button type="button" className={tabButtonClass(activeTab === 'consent')} onClick={() => goTab('consent')}>
          동의서 출력
        </button>
        <button type="button" className={tabButtonClass(activeTab === 'upload')} onClick={() => goTab('upload')}>
          동의서 업로드
        </button>
        {isOperator && (
          <button type="button" className={tabButtonClass(activeTab === 'setup')} onClick={() => goTab('setup')}>
            템플릿/좌표 설정(MVP)
          </button>
        )}
      </div>

      {activeTab === 'consent' && (
        <div className="rounded border p-4 space-y-4">
          <div className="text-sm text-slate-600">
            현재는 <span className="font-medium">기등록 고객 조회 없이</span> 고객 정보를 직접 입력해 동의서를 출력합니다. (추후 고객 불러오기/자동완성 기능 확장 예정)
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="text-xs text-slate-500">
              발행본이 없는 보험사는 선택할 수 없습니다. 먼저 발행본을 업로드해 주세요.
            </div>
            <button
              type="button"
              className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
              onClick={() => goTab('upload')}
            >
              동의서 업로드
            </button>
          </div>

          <form onSubmit={handleDownloadConsent} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm text-slate-700">성명(필수)</label>
                <input
                  className="w-full rounded border px-3 py-2"
                  value={form.customerName}
                  onChange={onChange('customerName')}
                  placeholder="예: 홍길동"
                  required
                />
              </div>

              {consentRequiredBindings.has('birthDate') && (
                <div className="space-y-1">
                  <label className="text-sm text-slate-700">생년월일(선택 보험사 필요)</label>
                  <input
                    type="date"
                    className="w-full rounded border px-3 py-2"
                    value={form.birthDate}
                    onChange={onChange('birthDate')}
                    required
                  />
                </div>
              )}

              {consentRequiredBindings.has('phoneNumber') && (
                <div className="space-y-1">
                  <label className="text-sm text-slate-700">연락처(선택 보험사 필요)</label>
                  <input
                    className="w-full rounded border px-3 py-2"
                    value={form.phoneNumber}
                    onChange={onChange('phoneNumber')}
                    placeholder="예: 010-1234-5678"
                    required
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="text-sm text-slate-700">보험사 선택(멀티)</label>
                <div className="rounded border p-3 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                    <button
                      type="button"
                      className="rounded border px-2 py-1 hover:bg-slate-50"
                      onClick={() => {
                        const ids = lifeInsurersSorted.map(o => String(o.id)).filter(id => issuedAvailableOrgIds.has(id))
                        setSelectedInsurerOrgIds(ids)
                      }}
                      disabled={loadingOrgs}
                    >
                      생보 전체 선택
                    </button>
                    <button
                      type="button"
                      className="rounded border px-2 py-1 hover:bg-slate-50"
                      onClick={() => {
                        const ids = nonlifeInsurersSorted.map(o => String(o.id)).filter(id => issuedAvailableOrgIds.has(id))
                        setSelectedInsurerOrgIds(ids)
                      }}
                      disabled={loadingOrgs}
                    >
                      손보 전체 선택
                    </button>
                    <button
                      type="button"
                      className="rounded border px-2 py-1 hover:bg-slate-50"
                      onClick={() => {
                        const ids = insurerCompanies.map(o => String(o.id)).filter(id => issuedAvailableOrgIds.has(id))
                        setSelectedInsurerOrgIds(ids)
                      }}
                      disabled={loadingOrgs}
                    >
                      전체 선택
                    </button>
                    <button
                      type="button"
                      className="rounded border px-2 py-1 hover:bg-slate-50"
                      onClick={() => setSelectedInsurerOrgIds([])}
                      disabled={loadingOrgs}
                    >
                      선택 해제
                    </button>
                    <span className="text-slate-400">|</span>
                    <span>선택: {selectedInsurerOrgIds.length}개</span>
                    <span className="text-slate-400">|</span>
                    <span className="text-slate-500">발행본 미업로드 보험사는 선택 불가</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-semibold text-slate-700 mb-2">생보</div>
                      <div className="max-h-48 overflow-auto space-y-1">
                        {lifeInsurersSorted.map((o) => {
                          const id = String(o.id)
                          const disabled = !issuedAvailableOrgIds.has(id)
                          const checked = selectedInsurerOrgIds.includes(id)
                          const st = issuedStatusByOrgId[id]
                          const uploadedAt = st?.updatedAt ? formatKst(st.updatedAt) : ''
                          return (
                            <label key={id} className={`flex items-center gap-2 text-sm ${disabled ? 'text-slate-400' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => {
                                  setSelectedInsurerOrgIds((prev) => {
                                    const set = new Set(prev)
                                    if (set.has(id)) set.delete(id)
                                    else set.add(id)
                                    return Array.from(set)
                                  })
                                }}
                              />
                              <span>{o.name}</span>
                              {disabled
                                ? <span className="text-xs">(발행본 없음)</span>
                                : <span className="text-xs text-slate-500">(업로드: {uploadedAt || '—'})</span>}
                            </label>
                          )
                        })}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-700 mb-2">손보</div>
                      <div className="max-h-48 overflow-auto space-y-1">
                        {nonlifeInsurersSorted.map((o) => {
                          const id = String(o.id)
                          const disabled = !issuedAvailableOrgIds.has(id)
                          const checked = selectedInsurerOrgIds.includes(id)
                          const st = issuedStatusByOrgId[id]
                          const uploadedAt = st?.updatedAt ? formatKst(st.updatedAt) : ''
                          return (
                            <label key={id} className={`flex items-center gap-2 text-sm ${disabled ? 'text-slate-400' : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => {
                                  setSelectedInsurerOrgIds((prev) => {
                                    const set = new Set(prev)
                                    if (set.has(id)) set.delete(id)
                                    else set.add(id)
                                    return Array.from(set)
                                  })
                                }}
                              />
                              <span>{o.name}</span>
                              {disabled
                                ? <span className="text-xs">(발행본 없음)</span>
                                : <span className="text-xs text-slate-500">(업로드: {uploadedAt || '—'})</span>}
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  {(!loadingOrgs && insurerCompanies.length === 0) && (
                    <div className="text-xs text-red-500">표시할 보험사 목록이 없습니다. (조직 데이터/권한을 확인해주세요)</div>
                  )}
                </div>
              </div>
            </div>

            {/* 보험사별 추가 필드 입력 */}
            {selectedInsurerOrgIds.length > 0 && (
              <div className="rounded border p-3 space-y-3">
                <div className="text-sm font-semibold">추가 필드(선택 보험사 기준)</div>
                <div className="text-xs text-slate-500">
                  선택한 보험사 템플릿에 있는 입력필드(bindKey)를 합쳐 1번만 입력합니다. 같은 필드가 여러 보험사에 있으면 하나의 입력으로 공유됩니다.
                </div>
                {(() => {
                  const extras = consentDynamicMeta.filter(m => !m.isCommon && m.bindKey !== 'customerName')
                  if (extras.length === 0) return <div className="text-xs text-slate-500">추가 입력 필드 없음</div>
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {extras.map((m) => {
                        const binding = m.bindKey
                        const val = (extraInputs || {})[binding] ?? ''
                        const insurerNames = Array.from(m.insurers || []).map(id => insurerNameById.get(String(id)) || String(id))
                        return (
                          <div key={binding} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <label className="text-xs text-slate-700">
                                {m.label}
                                {consentRequiredBindings.has(binding) ? <span className="text-rose-600"> *</span> : null}
                              </label>
                              <span className="text-[10px] text-slate-500">bind: {binding}</span>
                            </div>
                            <div className="text-[11px] text-slate-500">사용 보험사: {insurerNames.join(', ')}</div>
                            <input
                              className="w-full rounded border px-3 py-2 text-sm"
                              value={val}
                              onChange={(e) => {
                                const v = e.target.value
                                setExtraInputs((prev) => ({ ...(prev || {}), [binding]: v }))
                              }}
                              required={consentRequiredBindings.has(binding)}
                            />
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                type="submit"
                className="rounded bg-brand-navy text-white px-4 py-2 text-sm disabled:opacity-60"
                disabled={downloading || loadingOrgs}
              >
                {downloading ? '다운로드 준비 중…' : '동의서 다운로드'}
              </button>
              <button
                type="button"
                className="rounded border px-4 py-2 text-sm disabled:opacity-60"
                disabled={previewing || loadingOrgs}
                onClick={handlePreviewConsent}
              >
                {previewing ? '미리보기 생성 중…' : '미리보기(다운로드 전)'}
              </button>
              <div className="text-xs text-slate-500">
                출력본은 서버에 저장하지 않고 다운로드로만 제공합니다(정책).
              </div>
            </div>
          </form>

          {selectedInsurerOrgIds.length > 0 && (
            <div className="rounded border p-3 space-y-2 bg-slate-50">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold">출력 프리뷰</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-600">선택 보험사: {selectedInsurerOrgIds.length}개</span>
                </div>
              </div>
              <div className="text-xs text-slate-600">
                “미리보기(다운로드 전)”를 누르면 팝업에서 보험사를 바꿔가며 프리뷰/다운로드를 할 수 있습니다.
              </div>
            </div>
          )}

          {previewModalOpen && (
            <div
              className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4"
              onMouseDown={() => setPreviewModalOpen(false)}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-[96vw] max-w-6xl max-h-[92vh] overflow-auto"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-semibold">출력 프리뷰</div>
                    <span className="text-xs text-slate-600">보험사</span>
                    <select
                      className="rounded border px-2 py-1 text-sm"
                      value={previewInsurerOrgId || (Array.isArray(selectedInsurerOrgIds) ? selectedInsurerOrgIds[0] : '')}
                      onChange={(e) => {
                        const v = e.target.value
                        setPreviewModalErr('')
                        setPreviewInsurerOrgId(v)
                      }}
                      disabled={previewing || downloadingOneInModal || downloadingAllInModal}
                    >
                      {(selectedInsurerOrgIds || []).map((id) => (
                        <option key={id} value={id}>{insurerNameById.get(String(id)) || String(id)}</option>
                      ))}
                    </select>
                    {(() => {
                      const id = String(previewInsurerOrgId || (Array.isArray(selectedInsurerOrgIds) ? selectedInsurerOrgIds[0] : ''))
                      const st = issuedStatusByOrgId[String(id)]
                      return (
                        <span className="text-xs text-slate-500">
                          발행본: {issuedAvailableOrgIds.has(String(id)) ? `있음(업로드 ${st?.updatedAt ? formatKst(st.updatedAt) : '—'})` : '없음'}
                        </span>
                      )
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded border px-3 py-1.5 text-sm disabled:opacity-60"
                      disabled={downloadingOneInModal || downloadingAllInModal || previewing || !issuedAvailableOrgIds.has(String(previewInsurerOrgId || (selectedInsurerOrgIds || [])[0] || ''))}
                      onClick={async () => {
                        const id = String(previewInsurerOrgId || (selectedInsurerOrgIds || [])[0] || '')
                        if (!id) return
                        try {
                          setDownloadingOneInModal(true)
                          const bytes = previewPdfBytesByOrgId[id] || await renderPreviewPdf(id)
                          downloadPdfBytes(bytes, buildConsentFilename(id))
                        } catch (e) {
                          console.error(e)
                          show('개별 다운로드에 실패했습니다.', 'error')
                        } finally {
                          setDownloadingOneInModal(false)
                        }
                      }}
                    >
                      개별 다운로드
                    </button>
                    <button
                      type="button"
                      className="rounded bg-brand-navy text-white px-3 py-1.5 text-sm disabled:opacity-60"
                      disabled={downloadingAllInModal || downloadingOneInModal || previewing || !(Array.isArray(selectedInsurerOrgIds) && selectedInsurerOrgIds.length > 0)}
                      onClick={async () => {
                        try {
                          setDownloadingAllInModal(true)
                          const ids = (selectedInsurerOrgIds || []).filter(id => issuedAvailableOrgIds.has(String(id)))
                          if (ids.length === 0) {
                            show('다운로드 가능한 보험사가 없습니다. (발행본 업로드 필요)', 'error')
                            return
                          }
                          for (const id of ids) {
                            // eslint-disable-next-line no-await-in-loop
                            const bytes = previewPdfBytesByOrgId[String(id)] || await renderPreviewPdf(String(id))
                            downloadPdfBytes(bytes, buildConsentFilename(id))
                          }
                          show(`전체 다운로드가 시작되었습니다. (${ids.length}건)`, 'success')
                        } catch (e) {
                          console.error(e)
                          show('전체 다운로드에 실패했습니다.', 'error')
                        } finally {
                          setDownloadingAllInModal(false)
                        }
                      }}
                    >
                      전체 다운로드
                    </button>
                    <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={() => setPreviewModalOpen(false)}>
                      닫기
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {previewModalErr && (
                    <div className="rounded border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm mb-3">
                      {previewModalErr}
                    </div>
                  )}
                  {(() => {
                    const id = String(previewInsurerOrgId || (Array.isArray(selectedInsurerOrgIds) ? selectedInsurerOrgIds[0] : ''))
                    const bytes = id ? previewPdfBytesByOrgId[id] : null
                    if (!bytes) {
                      return <div className="text-sm text-slate-600">{previewing ? '프리뷰 생성 중…' : '프리뷰를 생성할 수 없습니다.'}</div>
                    }
                    return <PdfBytesViewer title="미리보기 결과(PDF)" pdfBytes={bytes} filenameHint={previewFilenameHint} />
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'setup' && (
        <div className="rounded border p-4 space-y-4">
          <div className="text-sm text-slate-700">
            이 탭은 운영자용 MVP입니다. <span className="font-medium">박스를 드래그해서 좌표를 자동 입력</span>할 수 있고, 필드는 자유롭게 추가할 수 있습니다.
          </div>

          {/* 1컬럼(전체폭) 레이아웃: PDF 작업 공간을 넓게 */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm text-slate-700">보험사 선택</label>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={setup.insurerOrgId}
                  onChange={(e) => {
                    const nextId = String(e.target.value || '')
                    // 보험사 변경 시, 다른 보험사의 좌표/필드가 남지 않도록 즉시 초기화한다.
                    lastSetupAutoLoadedInsurerIdRef.current = null
                    setSelectedFieldKey('customerName')
                    setSetupCurrentPageIndex(0)
                    setSetup((prev) => ({
                      ...prev,
                      insurerOrgId: nextId,
                      templateFileId: '',
                      fields: defaultConsentTemplateFields(),
                    }))
                  }}
                  disabled={loadingOrgs}
                >
                  <option value="">{loadingOrgs ? '불러오는 중…' : '보험사를 선택하세요'}</option>
                  {insurerCompanies.map((o) => {
                    const id = String(o.id)
                    const tplInfo = templatesByInsurer[id]
                    const tplOk = !!tplInfo?.raw?.templateFileId
                    const tplSuffix = tplInfo ? (tplOk ? ' (템플릿 있음)' : ' (템플릿 없음)') : ''
                    return (
                      <option key={o.id} value={id}>{o.name}{tplSuffix}</option>
                    )
                  })}
                </select>
                {setup.insurerOrgId && (
                  <div className="text-xs mt-1">
                    {(() => {
                      const id = String(setup.insurerOrgId)
                      const st = issuedStatusByOrgId[id]
                      const uploadedAt = st?.updatedAt ? formatKst(st.updatedAt) : ''
                      const tpl = templatesByInsurer[id]?.raw
                      const tplFileId = tpl?.templateFileId ? String(tpl.templateFileId) : ''
                      if (issuedAvailableOrgIds.has(id)) {
                        return (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="inline-flex items-center rounded bg-emerald-50 text-emerald-700 px-2 py-1 border border-emerald-200">발행본 있음 (업로드: {uploadedAt || '—'})</span>
                            {tplFileId
                              ? (
                                <>
                                  <span className="inline-flex items-center rounded bg-emerald-50 text-emerald-700 px-2 py-1 border border-emerald-200">템플릿 있음</span>
                                  <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => openFilePreview(tplFileId)}>
                                    템플릿 PDF 미리보기
                                  </button>
                                </>
                              )
                              : <span className="inline-flex items-center rounded bg-rose-50 text-rose-700 px-2 py-1 border border-rose-200">템플릿 없음</span>}
                          </div>
                        )
                      }
                      return (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center rounded bg-rose-50 text-rose-700 px-2 py-1 border border-rose-200">발행본 없음</span>
                          {tplFileId
                            ? (
                              <>
                                <span className="inline-flex items-center rounded bg-emerald-50 text-emerald-700 px-2 py-1 border border-emerald-200">템플릿 있음</span>
                                <button type="button" className="rounded border px-2 py-1 text-xs" onClick={() => openFilePreview(tplFileId)}>
                                  템플릿 PDF 미리보기
                                </button>
                              </>
                            )
                            : <span className="inline-flex items-center rounded bg-rose-50 text-rose-700 px-2 py-1 border border-rose-200">템플릿 없음</span>}
                        </div>
                      )
                    })()}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded border p-3 space-y-3">
              <div className="font-semibold">1) 템플릿 PDF(좌표 기준) 업로드</div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    try {
                      setUploadingTemplate(true)
                      const fileId = await uploadFile(f)
                      setSetup((prev) => ({ ...prev, templateFileId: fileId }))
                      show('템플릿 PDF 업로드 완료', 'success')
                    } catch (err) {
                      console.error(err)
                      const msg = err?.message ? String(err.message) : ''
                      if (msg.includes('api_misconfigured_html_response')) {
                        show('템플릿 PDF 업로드 실패: 스테이징 CloudFront/프록시 설정 확인 필요(HTML 응답)', 'error')
                      } else {
                        show(`템플릿 PDF 업로드 실패${msg ? ` (${msg})` : ''}`, 'error')
                      }
                    } finally {
                      setUploadingTemplate(false)
                      e.target.value = ''
                    }
                  }}
                  disabled={uploadingTemplate}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-600">templateFileId</label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 rounded border px-3 py-2 text-sm"
                    value={setup.templateFileId}
                    onChange={onSetupChange('templateFileId')}
                    placeholder="예: f_xxx.pdf"
                  />
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm"
                    disabled={!setup.templateFileId}
                    onClick={() => openFilePreview(setup.templateFileId)}
                  >
                    미리보기
                  </button>
                </div>
              </div>

              <div className="font-semibold mt-2">2) 좌표(필드) 입력</div>
              <div className="text-xs text-slate-500">
                필드 키 예시: <code className="px-1">customerName</code>, <code className="px-1">consentCheck1</code>~<code className="px-1">consentCheck5</code>, <code className="px-1">printedAtYear</code>(YYYY)/<code className="px-1">printedAtYear2</code>(YY)/<code className="px-1">printedAtMonth</code>/<code className="px-1">printedAtDay</code>, <code className="px-1">actorName</code>
              </div>

              {/* PDF 드래그 매핑 */}
              {setup.templateFileId ? (
                <PdfTemplateMapper
                  fileId={setup.templateFileId}
                  fields={setup.fields}
                  selectedKey={selectedFieldKey}
                  onSelectKey={(k) => { setSelectedFieldKey(k); ensureField(k) }}
                  onSetBox={(k, box) => {
                    ensureField(k)
                    updateField(k, { ...box })
                  }}
                  previewEnabled={setupPreviewEnabled}
                  previewValues={setupPreviewValues}
                  onPageIndexChange={(idx) => setSetupCurrentPageIndex(idx)}
                />
              ) : (
                <div className="text-xs text-slate-500">템플릿 PDF를 업로드하면 여기서 드래그로 좌표를 지정할 수 있습니다.</div>
              )}

              <div className="rounded border p-3 bg-slate-50 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">오버레이 미리보기(임의값)</div>
                  <label className="text-xs text-slate-700 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={setupPreviewEnabled}
                      onChange={(e) => setSetupPreviewEnabled(e.target.checked)}
                    />
                    표시
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <div className="text-[11px] text-slate-600">customerName</div>
                    <input className="w-full rounded border px-2 py-1 text-sm" value={setupPreviewValues.customerName}
                      onChange={(e) => setSetupPreviewValues((p) => ({ ...p, customerName: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[11px] text-slate-600">actorName(로그인유저)</div>
                    <input className="w-full rounded border px-2 py-1 text-sm" value={setupPreviewValues.actorName}
                      onChange={(e) => setSetupPreviewValues((p) => ({ ...p, actorName: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[11px] text-slate-600">기타(__other)</div>
                    <input className="w-full rounded border px-2 py-1 text-sm" value={setupPreviewValues.__other}
                      onChange={(e) => setSetupPreviewValues((p) => ({ ...p, __other: e.target.value }))} />
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">
                  printedAt/TODAY는 오늘 날짜, printedAtYear(YYYY)/printedAtYear2(YY)/printedAtMonth/printedAtDay(TODAY_YYYY/YY/MM/DD)는 오늘의 년/월/일이 기본값으로 자동 표시됩니다. consentCheck*/CHECK_V는 V로 자동 표시됩니다.
                </div>
              </div>
              <div className="text-xs text-slate-500">
                - 새 박스: PDF 빈 영역에서 드래그<br />
                - 이동: 기존 박스를 드래그해서 위치 이동
              </div>

              <div className="rounded border p-3 bg-slate-50">
                <div className="text-sm font-medium">선택된 필드</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {['customerName', 'consentCheck1', 'consentCheck2', 'consentCheck3', 'consentCheck4', 'consentCheck5'].map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`rounded border px-3 py-1 text-sm ${selectedFieldKey === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}
                      onClick={() => { setSelectedFieldKey(k); ensureField(k) }}
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <div className="mt-2 text-xs text-slate-600">
                  PDF 위에서 드래그하면 <span className="font-medium">{selectedFieldKey}</span> 필드에 좌표가 들어갑니다.
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1 text-left">key</th>
                      <th className="px-2 py-1 text-left">p</th>
                      <th className="px-2 py-1 text-left">x</th>
                      <th className="px-2 py-1 text-left">y</th>
                      <th className="px-2 py-1 text-left">w</th>
                      <th className="px-2 py-1 text-left">h</th>
                      <th className="px-2 py-1 text-left">font</th>
                      <th className="px-2 py-1 text-left">align</th>
                      <th className="px-2 py-1 text-left">bind</th>
                      <th className="px-2 py-1 text-left">default</th>
                      <th className="px-2 py-1 text-left"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(setup.fields || []).map((f, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-2 py-1">
                          <input
                            className="w-36 rounded border px-2 py-1"
                            value={f.key}
                            onChange={(e) => setSetup((prev) => ({
                              ...prev,
                              fields: prev.fields.map((it, i) => i === idx ? { ...it, key: e.target.value } : it)
                            }))}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input className="w-12 rounded border px-2 py-1" value={f.pageIndex}
                            onChange={(e) => setSetup((prev) => ({
                              ...prev,
                              fields: prev.fields.map((it, i) => i === idx ? { ...it, pageIndex: e.target.value } : it)
                            }))} />
                        </td>
                        {['x', 'y', 'w', 'h'].map((k) => (
                          <td key={k} className="px-2 py-1">
                            <input className="w-16 rounded border px-2 py-1" value={f[k]}
                              onChange={(e) => setSetup((prev) => ({
                                ...prev,
                                fields: prev.fields.map((it, i) => i === idx ? { ...it, [k]: e.target.value } : it)
                              }))} />
                          </td>
                        ))}
                        <td className="px-2 py-1">
                          <input className="w-14 rounded border px-2 py-1" value={f.fontSize}
                            onChange={(e) => setSetup((prev) => ({
                              ...prev,
                              fields: prev.fields.map((it, i) => i === idx ? { ...it, fontSize: e.target.value } : it)
                            }))} />
                        </td>
                        <td className="px-2 py-1">
                          <select className="w-20 rounded border px-2 py-1" value={f.align}
                            onChange={(e) => setSetup((prev) => ({
                              ...prev,
                              fields: prev.fields.map((it, i) => i === idx ? { ...it, align: e.target.value } : it)
                            }))}>
                            <option value="left">left</option>
                            <option value="center">center</option>
                            <option value="right">right</option>
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <input
                            className="w-32 rounded border px-2 py-1"
                            value={f.bindKey || ''}
                            list="consent-bindkey-suggestions"
                            placeholder="(미지정)"
                            onChange={(e) => setSetup((prev) => ({
                              ...prev,
                              fields: prev.fields.map((it, i) => i === idx ? { ...it, bindKey: e.target.value } : it)
                            }))}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-2">
                            <select
                              className="w-28 rounded border px-2 py-1"
                              value={f.valueType || ''}
                              onChange={(e) => setSetup((prev) => ({
                                ...prev,
                                fields: prev.fields.map((it, i) => i === idx ? { ...it, valueType: e.target.value } : it)
                              }))}
                            >
                              <option value="">(없음)</option>
                              <option value="TODAY">TODAY(출력일)</option>
                              <option value="TODAY_YYYY">TODAY_YYYY(출력년)</option>
                              <option value="TODAY_YY">TODAY_YY(출력년-2자리)</option>
                              <option value="TODAY_MM">TODAY_MM(출력월)</option>
                              <option value="TODAY_DD">TODAY_DD(출력일-일)</option>
                              <option value="CHECK_V">CHECK_V(V)</option>
                              <option value="STATIC">STATIC(고정값)</option>
                            </select>
                            <input
                              className="w-40 rounded border px-2 py-1"
                              value={f.value || ''}
                              onChange={(e) => setSetup((prev) => ({
                                ...prev,
                                fields: prev.fields.map((it, i) => i === idx ? { ...it, value: e.target.value } : it)
                              }))}
                              disabled={(f.valueType || '') !== 'STATIC'}
                              placeholder={(f.valueType || '') === 'STATIC' ? '고정값' : '—'}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <button
                            type="button"
                            className="text-brand-navy hover:underline mr-2"
                            onClick={() => { setSelectedFieldKey(f.key); ensureField(f.key) }}
                          >
                            선택
                          </button>
                          <button type="button" className="text-red-600 hover:underline"
                            onClick={() => setSetup((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== idx) }))}>
                            삭제
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded border px-3 py-2 text-sm"
                  onClick={() => {
                    const newKey = makeUniqueFieldKey('field')
                    setSetup((prev) => ({
                      ...prev,
                      fields: [...(prev.fields || []), { key: newKey, bindKey: '', pageIndex: Math.max(0, Number(setupCurrentPageIndex ?? 0)), x: 0, y: 0, w: 0.1, h: 0.03, fontSize: 16, align: 'left', valueType: '', value: '' }]
                    }))
                    setSelectedFieldKey(newKey)
                  }}
                >
                  필드 추가
                </button>
                <input
                  className="rounded border px-3 py-2 text-sm"
                  placeholder="새 필드 key 입력 후 Enter"
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    const k = String(e.currentTarget.value || '').trim()
                    if (!k) return
                    ensureField(k)
                    setSelectedFieldKey(k)
                    e.currentTarget.value = ''
                  }}
                />
                <button
                  type="button"
                  className="rounded bg-slate-900 text-white px-3 py-2 text-sm disabled:opacity-60"
                  onClick={saveTemplate}
                  disabled={savingTemplate || uploadingTemplate}
                >
                  {savingTemplate ? '저장 중…' : '템플릿 저장'}
                </button>
              </div>
              <datalist id="consent-bindkey-suggestions">
                <option value="customerName" />
                <option value="birthDate" />
                <option value="phoneNumber" />
                <option value="printedAt" />
                <option value="printedAtYear" />
                <option value="printedAtYear2" />
                <option value="printedAtMonth" />
                <option value="printedAtDay" />
                <option value="actorName" />
                <option value="consentCheck" />
                <option value="consentCheck1" />
                <option value="consentCheck2" />
                <option value="consentCheck3" />
                <option value="consentCheck4" />
                <option value="consentCheck5" />
              </datalist>
            </div>

            <div className="rounded border p-3 space-y-3">
              <div className="font-semibold">3) 발행본 PDF 업로드(보험사/발행번호 포함)</div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]
                    if (!f) return
                    try {
                      setUploadingIssued(true)
                      const fileId = await uploadFile(f)
                      setSetup((prev) => ({ ...prev, issuedFileId: fileId }))
                      show('발행본 PDF 업로드 완료', 'success')
                    } catch (err) {
                      console.error(err)
                      show('발행본 PDF 업로드 실패', 'error')
                    } finally {
                      setUploadingIssued(false)
                      e.target.value = ''
                    }
                  }}
                  disabled={uploadingIssued}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-600">issuedFileId</label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 rounded border px-3 py-2 text-sm"
                    value={setup.issuedFileId}
                    onChange={onSetupChange('issuedFileId')}
                    placeholder="예: f_xxx.pdf"
                  />
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm"
                    disabled={!setup.issuedFileId}
                    onClick={() => openFilePreview(setup.issuedFileId)}
                  >
                    미리보기
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded bg-slate-900 text-white px-3 py-2 text-sm disabled:opacity-60"
                  onClick={saveIssued}
                  disabled={savingIssued || uploadingIssued}
                >
                  {savingIssued ? '저장 중…' : '발행본 설정'}
                </button>
              </div>

              <div className="rounded bg-slate-50 p-3 text-xs text-slate-600">
                <div className="font-semibold text-slate-700 mb-1">참고</div>
                <div>- 템플릿 저장: <code>/v1/consent-forms/templates</code></div>
                <div>- 발행본 설정: <code>/v1/consent-forms/issued-pdfs</code></div>
                <div>- 출력: <code>/v1/consent-forms/render</code></div>
              </div>
            </div>
          </div>

          <details className="rounded border p-3">
            <summary className="cursor-pointer text-sm font-medium">fields JSON 보기(디버그)</summary>
            <pre className="mt-3 text-xs overflow-auto bg-slate-950 text-slate-100 rounded p-3">{fieldsJson}</pre>
          </details>
        </div>
      )}

      {activeTab === 'upload' && (
        <div className="rounded border p-4 space-y-4">
          <div>
            <h2 className="text-lg font-semibold">동의서 업로드(발행본 PDF)</h2>
            <p className="text-sm text-slate-600 mt-1">
              보험사를 선택하고, 해당 보험사의 템플릿을 미리본 뒤 발행본 PDF를 업로드해 등록합니다. (발행본은 <span className="font-medium">사용자별</span>로 관리됩니다)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm text-slate-700">업로드할 보험사</label>
                {(() => {
                  const missing = (insurerCompanies || []).filter((o) => o?.id != null && !templateAvailableOrgIds.has(String(o.id)))
                  if (missing.length === 0) return null
                  return (
                    <button
                      type="button"
                      className="text-xs text-slate-600 hover:underline"
                      onClick={() => setNoTemplateModalOpen(true)}
                    >
                      템플릿 없는 보험사 목록({missing.length})
                    </button>
                  )
                })()}
              </div>
              <div className="flex items-center gap-2 mt-1">
                <label className="text-xs text-slate-600 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showNoTemplateInUploadList}
                    onChange={(e) => setShowNoTemplateInUploadList(e.target.checked)}
                  />
                  템플릿 없는 보험사도 목록에 표시
                </label>
              </div>
              <select
                className="w-full rounded border px-3 py-2"
                value={uploadInsurerOrgId}
                onChange={(e) => setUploadInsurerOrgId(e.target.value)}
                disabled={loadingOrgs}
              >
                <option value="">{loadingOrgs ? '불러오는 중…' : '보험사를 선택하세요'}</option>
                {(() => {
                  const list = Array.isArray(insurerCompanies) ? [...insurerCompanies] : []
                  // 업로드 탭에서는 "템플릿 있음"을 우선 노출하고, 그 다음 "내 발행본 없음" 등을 표시한다.
                  list.sort((a, b) => {
                    const aId = String(a?.id || '')
                    const bId = String(b?.id || '')
                    const aTpl = aId ? templateAvailableOrgIds.has(aId) : false
                    const bTpl = bId ? templateAvailableOrgIds.has(bId) : false
                    if (aTpl !== bTpl) return aTpl ? -1 : 1 // 템플릿 있음 먼저
                    const aHas = aId ? issuedAvailableOrgIds.has(aId) : false
                    const bHas = bId ? issuedAvailableOrgIds.has(bId) : false
                    if (aHas !== bHas) return aHas ? 1 : -1 // 없음 먼저
                    return String(a?.name || '').localeCompare(String(b?.name || ''))
                  })
                  return list.map((o) => {
                    const id = String(o.id)
                    const hasIssued = issuedAvailableOrgIds.has(id)
                    const hasTpl = templateAvailableOrgIds.has(id)
                    if (!hasTpl && !showNoTemplateInUploadList) return null
                    const label = `${o.name}${hasTpl ? '' : ' (템플릿 없음)'}${hasIssued ? '' : ' (내 발행본 없음)'}`
                    return <option key={o.id} value={id} disabled={!hasTpl}>{label}</option>
                  })
                })()}
              </select>
              {uploadInsurerOrgId && (
                <div className="text-xs mt-1">
                  {(() => {
                    const id = String(uploadInsurerOrgId)
                    const st = issuedStatusByOrgId[id]
                    const uploadedAt = st?.updatedAt ? formatKst(st.updatedAt) : ''
                    const hasTpl = templateAvailableOrgIds.has(id)
                    if (issuedAvailableOrgIds.has(id)) {
                      return <span className="inline-flex items-center rounded bg-emerald-50 text-emerald-700 px-2 py-1 border border-emerald-200">내 계정 기준 발행본 있음 (업로드: {uploadedAt || '—'})</span>
                    }
                    return (
                      <div className="flex flex-wrap items-center gap-2">
                        {!hasTpl && (
                          <span className="inline-flex items-center rounded bg-rose-50 text-rose-700 px-2 py-1 border border-rose-200">
                            활성 템플릿 없음(업로드 불가)
                          </span>
                        )}
                        <span className="inline-flex items-center rounded bg-amber-50 text-amber-700 px-2 py-1 border border-amber-200">내 계정 기준 발행본 없음</span>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          </div>

          {noTemplateModalOpen && (
            <div
              className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4"
              onMouseDown={() => setNoTemplateModalOpen(false)}
            >
              <div
                className="bg-white rounded-lg shadow-xl w-[92vw] max-w-2xl max-h-[86vh] overflow-auto"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                  <div className="font-semibold">템플릿 없는 보험사</div>
                  <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={() => setNoTemplateModalOpen(false)}>
                    닫기
                  </button>
                </div>
                <div className="p-4 space-y-2">
                  <div className="text-sm text-slate-600">
                    아래 보험사는 현재 <span className="font-medium">활성 템플릿(좌표 설정)</span>이 없어, 발행본 업로드를 진행할 수 없습니다.
                  </div>
                  <div className="rounded border divide-y">
                    {(insurerCompanies || [])
                      .filter((o) => o?.id != null && !templateAvailableOrgIds.has(String(o.id)))
                      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
                      .map((o) => {
                        const id = String(o.id)
                        const override = (() => {
                          try {
                            // JSON은 { "27": "사유", ... } 형태로 관리
                            const v = noTemplateReasons?.[id]
                            return v != null ? String(v) : ''
                          } catch {
                            return ''
                          }
                        })()
                        const reason = templateStatusByOrgId[id]?.reason || 'TEMPLATE_NOT_FOUND'
                        const msg = override
                          ? override
                          : (reason === 'TEMPLATE_NOT_FOUND' ? '운영자 템플릿/좌표 설정 미등록' : reason)
                        return (
                          <div key={id} className="px-3 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium text-slate-800">{o.name}</div>
                              <div className="text-xs text-slate-500">orgId: {id}</div>
                            </div>
                            <div className="text-xs text-rose-700 mt-1">{msg}</div>
                          </div>
                        )
                      })}
                  </div>
                  <div className="text-xs text-slate-500">
                    해결 방법: 운영자에게 “템플릿/좌표 설정(활성 템플릿)” 등록을 요청해 주세요.
                  </div>
                </div>
              </div>
            </div>
          )}

          {uploadInsurerOrgId && (
            <div className="rounded border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold">1) 템플릿 확인</div>
                {(() => {
                  const tpl = templatesByInsurer[String(uploadInsurerOrgId)]?.raw
                  const templateFileId = tpl?.templateFileId
                  const ok = !!templateFileId
                  return (
                    <div className="flex items-center gap-2">
                      {ok
                        ? <span className="text-xs inline-flex items-center rounded bg-emerald-50 text-emerald-700 px-2 py-1 border border-emerald-200">활성 템플릿 있음</span>
                        : <span className="text-xs inline-flex items-center rounded bg-rose-50 text-rose-700 px-2 py-1 border border-rose-200">활성 템플릿 없음</span>}
                      <button
                        type="button"
                        className="rounded border px-3 py-1.5 text-sm disabled:opacity-60"
                        disabled={!ok}
                        onClick={() => openFilePreview(templateFileId)}
                      >
                        템플릿 PDF 미리보기
                      </button>
                    </div>
                  )
                })()}
              </div>

              <div className="text-xs text-slate-500">
                템플릿/좌표 설정에서 만든 필드(라벨=key, 저장키=bind)를 기준으로 동의서 출력 화면 입력이 구성됩니다.
              </div>

              {(() => {
                const fields = templatesByInsurer[String(uploadInsurerOrgId)]?.fields || []
                if (!Array.isArray(fields) || fields.length === 0) return <div className="text-xs text-slate-500">템플릿 필드 정보 없음</div>
                return (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1 text-left">라벨(key)</th>
                          <th className="px-2 py-1 text-left">bind</th>
                          <th className="px-2 py-1 text-left">p</th>
                          <th className="px-2 py-1 text-left">default</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((f, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-2 py-1">{String(f?.key || '')}</td>
                            <td className="px-2 py-1">{String(f?.bindKey || '')}</td>
                            <td className="px-2 py-1">{String(Number(f?.pageIndex ?? 0) + 1)}</td>
                            <td className="px-2 py-1">
                              {String(f?.valueType || '') === 'STATIC' ? `STATIC:${String(f?.value || '')}` : String(f?.valueType || '')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              })()}
            </div>
          )}

          {uploadInsurerOrgId && (
            <div className="rounded border p-3 space-y-3">
              <div className="font-semibold">2) 발행본 PDF 업로드</div>
              <div className="text-xs text-slate-500">
                업로드 후에는 이 화면/동의서 출력 탭에서 해당 보험사가 선택 가능해집니다.
              </div>
              {(() => {
                const tpl = templatesByInsurer[String(uploadInsurerOrgId)]?.raw
                const ok = !!tpl?.templateFileId
                if (ok) return null
                return (
                  <div className="text-xs text-rose-600">
                    활성 템플릿이 없어서 발행본 업로드를 진행할 수 없습니다. 운영자에게 템플릿/좌표 설정을 요청해 주세요.
                  </div>
                )
              })()}
              {uploadLayoutCheck?.status !== 'idle' && (
                <div className={`text-xs ${uploadLayoutCheck.status === 'ok' ? 'text-emerald-700' : (uploadLayoutCheck.status === 'ng' ? 'text-rose-700' : 'text-slate-600')}`}>
                  {uploadLayoutCheck.message}
                </div>
              )}
              <input
                type="file"
                accept="application/pdf"
                disabled={uploadingIssuedFromUploadPage || !templatesByInsurer[String(uploadInsurerOrgId)]?.raw?.templateFileId}
                onChange={async (e) => {
                  const f = e.target.files?.[0]
                  if (!f) return
                  try {
                    await handleUploadIssuedFromUploadPage(f)
                  } finally {
                    e.target.value = ''
                  }
                }}
              />
            </div>
          )}
        </div>
      )}
    </section>
  )
}
